import CreateCustomerDialog from "@/components/customers/CreateCustomerDialog";
import { BackArrow } from "@/components/icons/BackArrow";
import { MultiSelectCombobox } from "@/components/shadcn-custom/MultiSelectCombobox";
import { Avatar } from "@/components/shadcn-custom/ShadcnAvatar";
import { Checkbox } from "@/components/shadcn-custom/ShadcnCheckbox";
import { Combobox } from "@/components/shadcn-custom/ShadcnCombobox";
import { FileUploadItem } from "@/components/shadcn-custom/ShadcnFileLoader";
import { Radio } from "@/components/shadcn-custom/ShadcnRadio";
import { SegmentControl } from "@/components/shadcn-custom/ShadcnSegmentControl";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PlacesAutocomplete } from "@/components/ui/places-autocomplete";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { SearchableSelectPopover } from "@/components/ui/searchable-select-popover";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { AddWorkerToWorkUnitDialog } from "@/components/work-units/AddWorkerToWorkUnitDialog";
import { useAgencies } from "@/features/agencies/hooks/useAgencies";
import { useConfig, useProjectStatuses } from "@/features/config/hooks/useConfig";
import { useCustomerOperations } from "@/features/customers/hooks/useCustomers";
import { useProjects } from "@/features/projects/hooks/useProjects";
import { useUsers } from "@/features/users/hooks/useUsers";
import {
  workUnitService,
  type CreateWorkUnitInput,
} from "@/features/work-units/api/workUnitService";
import { useAllWorkers } from "@/features/workers/hooks/useWorkers";
import {
  EnglishLevel,
  WorkUnit as FirestoreWorkUnit,
  WorkUnitSchedule,
  WorkUnitWorkerAssignment,
  type ProjectJobLocation,
} from "@/firestore_types";
import {
  getJobLocationAddress,
  isProjectJobLocation,
  normalizeJobLocationForSave,
} from "@/lib/jobLocation";
import { WorkerShift } from "@/lib/projectWorkerUtils";
import { dateStringToNewYorkDate, formatTimeTo12Hour } from "@/lib/utils";
import { ProjectFormData } from "@/schemas/projectSchema";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { Timestamp } from "firebase/firestore";
import { Calendar, Clock } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

// Helper function to trigger native date/time picker
const triggerPicker = (elementId: string) => {
  const element = document.getElementById(elementId) as HTMLInputElement | null;
  if (element && "showPicker" in element) {
    (element as any).showPicker();
  }
};

export const Route = createFileRoute("/dashboard/admin/projects/create")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): { customerId?: string } => {
    return {
      customerId: (search.customerId as string) || undefined,
    };
  },
});

type Step = 1 | 2 | 3;

function RouteComponent() {
  const navigate = useNavigate();
  const { customerId: preselectedCustomerId } = Route.useSearch();
  const { createProject, creating } = useProjects();
  const { users } = useUsers();
  const { workers: allWorkers } = useAllWorkers();
  const { agencies } = useAgencies();
  const { projectStatuses } = useProjectStatuses();
  const { addContact, loading: contactLoading } = useCustomerOperations();
  const { config, addWorkType } = useConfig();

  // Responsive screen size state
  const [screenSize, setScreenSize] = useState<
    "mobile" | "tablet" | "desktop-small" | "desktop-large"
  >("desktop-large");

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      if (width < 390) {
        setScreenSize("mobile");
      } else if (width < 744) {
        setScreenSize("mobile");
      } else if (width < 1024) {
        setScreenSize("tablet");
      } else {
        setScreenSize("desktop-large");
      }
    };
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const isMobile = screenSize === "mobile";
  const isDesktop = screenSize === "desktop-large";

  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [useCustomerAddress, setUseCustomerAddress] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, boolean>>({});
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ id: string; file: File }>>([]);
  const [scheduleFormat, setScheduleFormat] = useState<"preset" | "custom">("preset");
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [allDay, setAllDay] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [presetStartTime, setPresetStartTime] = useState("");
  const [presetEndTime, setPresetEndTime] = useState("");
  const [presetBoundaryStartDate, setPresetBoundaryStartDate] = useState("");
  const [presetBoundaryEndDate, setPresetBoundaryEndDate] = useState("");

  // Custom schedule state
  const [customFrequency, setCustomFrequency] = useState<"weekly" | "monthly">("weekly");
  const [customEvery, setCustomEvery] = useState("3");
  const [selectedDays, setSelectedDays] = useState<string[]>(["mon", "tue", "wed"]);
  const [monthlyScheduleType, setMonthlyScheduleType] = useState<"by-date" | "by-day">("by-date");
  const [dayOfMonth, setDayOfMonth] = useState("3");
  const [monthlyWeek, setMonthlyWeek] = useState("first");
  const [monthlyDay, setMonthlyDay] = useState("monday");
  const [customAllDay, setCustomAllDay] = useState(true);
  const [customAdvanced, setCustomAdvanced] = useState(false);
  const [customStartTime, setCustomStartTime] = useState("");
  const [customEndTime, setCustomEndTime] = useState("");
  const [customBoundaryStartDate, setCustomBoundaryStartDate] = useState("");
  const [customBoundaryEndDate, setCustomBoundaryEndDate] = useState("");

  // Work unit dialog state
  const [workUnitDialogOpen, setWorkUnitDialogOpen] = useState(false);
  const [workUnitName, setWorkUnitName] = useState("");
  const [workersNeeded, setWorkersNeeded] = useState("1");
  const [jobDescription, setJobDescription] = useState("");
  const [englishLevel, setEnglishLevel] = useState("no-requirement");
  const [payRate, setPayRate] = useState("");
  const [workUnitStartDate, setWorkUnitStartDate] = useState("");
  const [hasSchedule, setHasSchedule] = useState(false);
  const [workUnitNameError, setWorkUnitNameError] = useState("");
  const [isCreatingNewWorkType, setIsCreatingNewWorkType] = useState(false);
  const [newWorkTypeName, setNewWorkTypeName] = useState("");
  const [workUnitTimeType, setWorkUnitTimeType] = useState("same");
  const [workUnitDays, setWorkUnitDays] = useState<string[]>(["mon", "tue", "wed"]);
  const [workUnitAllDay, setWorkUnitAllDay] = useState(false);
  const [workUnitStartTime, setWorkUnitStartTime] = useState("09:00");
  const [workUnitEndTime, setWorkUnitEndTime] = useState("17:00");
  // Per-day schedule for "Different per day" mode
  const [workUnitDaySchedules, setWorkUnitDaySchedules] = useState<
    Record<string, { allDay: boolean; startTime: string; endTime: string }>
  >({
    sun: { allDay: false, startTime: "09:00", endTime: "17:00" },
    mon: { allDay: false, startTime: "09:00", endTime: "17:00" },
    tue: { allDay: false, startTime: "09:00", endTime: "17:00" },
    wed: { allDay: false, startTime: "09:00", endTime: "17:00" },
    thu: { allDay: false, startTime: "09:00", endTime: "17:00" },
    fri: { allDay: false, startTime: "09:00", endTime: "17:00" },
    sat: { allDay: false, startTime: "09:00", endTime: "17:00" },
  });

  // Work units list
  interface Worker {
    id: string;
    name: string;
    initials: string;
    phone: string;
    avatar?: string;
    workerShifts?: WorkerShift[] | null;
    schedule?: WorkUnitSchedule;
  }
  interface FormWorkUnit {
    id: string;
    name: string;
    workersNeeded: number;
    englishLevel: string;
    payRate: string;
    startDate: string;
    description: string;
    hasSchedule: boolean;
    days: string[];
    allDay: boolean;
    startTime: string;
    endTime: string;
    expanded: boolean;
    workers: Worker[];
  }
  const [workUnits, setWorkUnits] = useState<FormWorkUnit[]>([]);

  // Add Worker dialog state
  const [addWorkerDialogOpen, setAddWorkerDialogOpen] = useState(false);
  const [addWorkerToUnitId, setAddWorkerToUnitId] = useState<string | null>(null);

  // Customer contact dialog state
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
  const [newContactData, setNewContactData] = useState({
    title: "",
    name: "",
    email: "",
    phone: "",
    notes: "",
  });

  const [formData, setFormData] = useState<ProjectFormData>({
    name: "",
    description: "",
    customerId: "",
    customerContactId: null,
    assignedManagerIds: [],
    assignedSupportUserIds: [],
    assignedAdminId: null,
    assignedAgencyId: null,
    isActive: true,
    jobLocation: null,
    statusId: "",
    type: "ongoing",
    startDate: "",
    endDate: "",
    notes: "",
    attachments: [],
    schedule: null,
    hasSchedule: false,
  });
  const buildJobLocationObject = (current: ProjectFormData["jobLocation"]): ProjectJobLocation => {
    if (isProjectJobLocation(current)) {
      return { ...current };
    }

    return {
      address: typeof current === "string" ? current : "",
      latitude: null,
      longitude: null,
    };
  };
  const jobLocationAddress = getJobLocationAddress(formData.jobLocation);
  const jobLocationNotes = isProjectJobLocation(formData.jobLocation)
    ? (formData.jobLocation.addressNotes ?? "")
    : "";
  const jobLocationCity = isProjectJobLocation(formData.jobLocation)
    ? (formData.jobLocation.city ?? "")
    : "";
  const jobLocationState = isProjectJobLocation(formData.jobLocation)
    ? (formData.jobLocation.state ?? "")
    : "";
  const jobLocationZip = isProjectJobLocation(formData.jobLocation)
    ? (formData.jobLocation.zipCode ?? "")
    : "";
  const formatCustomerAddress = useCallback(
    (customerId: string) => {
      const customer = users.find((u) => u.uid === customerId);
      if (!customer?.address) return null;

      const { street, address2, city, state, zipCode, country } = customer.address;
      const parts = [street, address2, city, state, zipCode, country].filter(Boolean);

      return {
        address: parts.join(", "),
        city: city || "",
        state: state || "",
        zipCode: zipCode || "",
      };
    },
    [users],
  );
  const [smsNotifications, setSmsNotifications] = useState(true);

  const customerUsers = users.filter((user) => user.type === "customer");
  const managerUsers = users.filter((user) => user.type === "manager");
  const supportUsers = users.filter((user) => user.type === "support");
  const adminUsers = users.filter((user) => user.type === "admin");

  // Pre-select the default status when projectStatuses load
  useEffect(() => {
    if (projectStatuses.length > 0 && !formData.statusId) {
      const defaultStatus = projectStatuses.find((s) => s.isDefault);
      if (defaultStatus) {
        setFormData((prev) => ({ ...prev, statusId: defaultStatus.id }));
      }
    }
  }, [projectStatuses, formData.statusId]);

  // Pre-select customer if provided via search params
  useEffect(() => {
    if (preselectedCustomerId && !formData.customerId) {
      setFormData((prev) => ({ ...prev, customerId: preselectedCustomerId }));
    }
  }, [preselectedCustomerId, formData.customerId]);

  useEffect(() => {
    if (useCustomerAddress && formData.customerId) {
      const formattedAddress = formatCustomerAddress(formData.customerId);
      if (formattedAddress) {
        setFormData((prev) => {
          const currentLocation = buildJobLocationObject(prev.jobLocation);
          return {
            ...prev,
            jobLocation: {
              ...currentLocation,
              address: formattedAddress.address,
              city: formattedAddress.city || currentLocation.city,
              state: formattedAddress.state || currentLocation.state,
              zipCode: formattedAddress.zipCode || currentLocation.zipCode,
            },
          };
        });
      }
    }
  }, [useCustomerAddress, formData.customerId, formatCustomerAddress]);

  // Calculate preview of next occurrences
  const previewDates = useMemo(() => {
    // If we are in custom mode but required fields are missing
    if (scheduleFormat === "custom" && customFrequency === "weekly" && selectedDays.length === 0) {
      return [];
    }

    const isPreset = scheduleFormat === "preset";

    // Effective start/end dates
    const boundaryStart = isPreset ? presetBoundaryStartDate : customBoundaryStartDate;
    const boundaryEnd = isPreset ? presetBoundaryEndDate : customBoundaryEndDate;

    // Initialize current date from boundary start, or project start, or today
    let start: Date;
    if (boundaryStart) {
      const [y, m, d] = boundaryStart.split("-").map(Number);
      start = new Date(y, m - 1, d);
    } else if (formData.startDate) {
      const [y, m, d] = formData.startDate.split("-").map(Number);
      start = new Date(y, m - 1, d);
    } else {
      const now = new Date();
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    }

    // Determine end date
    let end: Date | null = null;
    if (boundaryEnd) {
      const [y, m, d] = boundaryEnd.split("-").map(Number);
      end = new Date(y, m - 1, d);
    } else if (formData.endDate) {
      const [y, m, d] = formData.endDate.split("-").map(Number);
      end = new Date(y, m - 1, d);
    }

    const preview: Date[] = [];
    let current = new Date(start);
    const maxPreview = 5;

    if (isPreset) {
      if (!selectedPreset) return [];

      switch (selectedPreset) {
        case "daily":
          for (let i = 0; i < maxPreview && (!end || current <= end); i++) {
            preview.push(new Date(current));
            current.setDate(current.getDate() + 1);
          }
          break;
        case "weekdays":
          while (preview.length < maxPreview && (!end || current <= end)) {
            const day = current.getDay();
            if (day >= 1 && day <= 5) preview.push(new Date(current));
            current.setDate(current.getDate() + 1);
          }
          break;
        case "weekends":
          while (preview.length < maxPreview && (!end || current <= end)) {
            const day = current.getDay();
            if (day === 0 || day === 6) preview.push(new Date(current));
            current.setDate(current.getDate() + 1);
          }
          break;
        case "weekly":
          for (let i = 0; i < maxPreview && (!end || current <= end); i++) {
            preview.push(new Date(current));
            current.setDate(current.getDate() + 7);
          }
          break;
        case "monthly":
          for (let i = 0; i < maxPreview && (!end || current <= end); i++) {
            preview.push(new Date(current));
            current.setMonth(current.getMonth() + 1);
          }
          break;
      }
    } else {
      // Custom
      if (customFrequency === "weekly" && selectedDays.length > 0) {
        const dayMap: Record<string, number> = {
          sun: 0,
          mon: 1,
          tue: 2,
          wed: 3,
          thu: 4,
          fri: 5,
          sat: 6,
        };
        const targetDays = selectedDays.map((d) => dayMap[d]);

        while (preview.length < maxPreview && (!end || current <= end)) {
          if (targetDays.includes(current.getDay())) {
            preview.push(new Date(current));
          }
          current.setDate(current.getDate() + 1);
        }
      }
      // Note: ScheduleForm does not implement preview for Custom Monthly frequency, so we skip it here too for consistency
    }
    return preview;
  }, [
    scheduleFormat,
    selectedPreset,
    customFrequency,
    selectedDays,
    presetBoundaryStartDate,
    customBoundaryStartDate,
    presetBoundaryEndDate,
    customBoundaryEndDate,
    formData.startDate,
    formData.endDate,
  ]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };

      // For one-time projects, automatically set end date same as start date
      // Only if end date is not set or is before the new start date
      if (field === "startDate" && prev.type === "one-time" && value) {
        if (!prev.endDate || prev.endDate < value) {
          updated.endDate = value;
        }
      }

      return updated;
    });
  };

  const handleCustomerCreated = (customerUid: string) => {
    console.log("New customer created with uid:", customerUid);
    handleInputChange("customerId", customerUid);
    toast.success("Customer created successfully and selected!");
  };

  const handleAddNewContact = useCallback(async () => {
    if (!formData.customerId || !newContactData.name.trim()) {
      toast.error("Please select a customer and provide at least a name for the contact");
      return;
    }

    try {
      const result = await addContact(formData.customerId, {
        title: newContactData.title.trim() || null,
        name: newContactData.name.trim(),
        email: newContactData.email.trim() || null,
        phone: newContactData.phone.trim() || null,
        notes: newContactData.notes.trim() || null,
      });

      if (result.success && result.data?.contactId) {
        toast.success("Contact added successfully!");

        // Auto-select the newly created contact
        handleInputChange("customerContactId", result.data.contactId);

        setIsContactDialogOpen(false);
        setNewContactData({
          title: "",
          name: "",
          email: "",
          phone: "",
          notes: "",
        });
      } else {
        toast.error("Failed to add contact. Please try again.");
      }
    } catch (error) {
      console.error("Error adding contact:", error);
      toast.error("Failed to add contact. Please try again.");
    }
  }, [formData.customerId, newContactData, addContact]);

  const handleContactCancel = useCallback(() => {
    setIsContactDialogOpen(false);
    setNewContactData({
      title: "",
      name: "",
      email: "",
      phone: "",
      notes: "",
    });
  }, []);

  const handleNext = () => {
    if (currentStep < 3) {
      if (isStepComplete(currentStep)) {
        setCurrentStep((prev) => (prev + 1) as Step);
        setValidationErrors({});
      } else {
        // Show validation errors for required fields
        const errors: Record<string, boolean> = {};
        if (currentStep === 1) {
          if (!formData.name) errors.name = true;
          if (!formData.assignedManagerIds || formData.assignedManagerIds.length === 0)
            errors.assignedManagerIds = true;
          if (!formData.customerId) errors.customerId = true;
        } else if (currentStep === 2) {
          // Validate date range
          if (formData.startDate && formData.endDate) {
            const start = new Date(formData.startDate);
            const end = new Date(formData.endDate);
            if (end < start) {
              errors.endDate = true;
              toast.error("End date cannot be before start date");
            }
          }
        }
        setValidationErrors(errors);
      }
    }
  };

  const handleCancel = () => {
    navigate({ to: "/dashboard/admin/projects" });
  };

  const handleAssignWorker = async (assignment: WorkUnitWorkerAssignment) => {
    if (!addWorkerToUnitId) return;

    const selectedUnit = workUnits.find((u) => u.id === addWorkerToUnitId);
    if (!selectedUnit) return;

    const workerDetails = allWorkers.find((w) => w.id === assignment.workerId);
    if (!workerDetails) return;

    // Check if worker already exists in this unit
    if (selectedUnit.workers.some((w) => w.id === assignment.workerId)) {
      toast.error("Worker already assigned to this unit");
      return;
    }

    const newWorker: Worker = {
      id: workerDetails.id,
      name: workerDetails.name,
      initials: workerDetails.name
        .split(" ")
        .map((n: string) => n[0])
        .join("")
        .substring(0, 2)
        .toUpperCase(),
      phone: workerDetails.phoneNumber,
      avatar: workerDetails.profilePicture || undefined,
      workerShifts: assignment.workerShifts,
      schedule: assignment.schedule,
    };

    setWorkUnits((prev) =>
      prev.map((u) =>
        u.id === addWorkerToUnitId ? { ...u, workers: [...u.workers, newWorker] } : u,
      ),
    );
  };

  const handleCreateNewWorkType = async () => {
    if (!newWorkTypeName.trim()) {
      toast.error("Please enter a work type name");
      return;
    }

    try {
      await addWorkType(newWorkTypeName.trim());
      setWorkUnitName(newWorkTypeName.trim());
      setNewWorkTypeName("");
      setIsCreatingNewWorkType(false);
      toast.success("Work type created and selected");
    } catch (error) {
      toast.error("Failed to create work type");
    }
  };

  // Helper function to convert form English level to EnglishLevel type
  const mapEnglishLevel = (formValue: string): EnglishLevel | undefined => {
    const mapping: Record<string, EnglishLevel> = {
      "no-requirement": "No English",
      basic: "Basic",
      intermediate: "Conversational", // UI shows 'Intermediate' but maps to Conversational
      advanced: "Fluent",
      fluent: "Native",
    };
    return mapping[formValue];
  };

  // Helper function to map day strings to day numbers
  const mapDaysToDayNumbers = (days: string[]): (0 | 1 | 2 | 3 | 4 | 5 | 6)[] => {
    const dayMap: Record<string, 0 | 1 | 2 | 3 | 4 | 5 | 6> = {
      sun: 0,
      mon: 1,
      tue: 2,
      wed: 3,
      thu: 4,
      fri: 5,
      sat: 6,
    };
    // Filter out any unrecognized day strings
    return days
      .map((d) => dayMap[d.toLowerCase()])
      .filter((num): num is 0 | 1 | 2 | 3 | 4 | 5 | 6 => num !== undefined);
  };

  const handleSubmit = async () => {
    try {
      const projectId = await createProject({
        name: formData.name,
        description: formData.description,
        customerId: formData.customerId || null,
        customerContactId: formData.customerContactId || null,
        assignedManagerIds: formData.assignedManagerIds,
        assignedSupportUserIds: formData.assignedSupportUserIds,
        assignedAdminId: formData.assignedAdminId,
        assignedAgencyId: formData.assignedAgencyId ?? null,
        isActive: true,
        jobLocation: normalizeJobLocationForSave(formData.jobLocation),
        statusId: formData.statusId,
        type: formData.type || "ongoing",
        startDate:
          formData.startDate && formData.startDate.trim() !== ""
            ? Timestamp.fromDate(dateStringToNewYorkDate(formData.startDate))
            : null,
        endDate:
          formData.endDate && formData.endDate.trim() !== ""
            ? Timestamp.fromDate(dateStringToNewYorkDate(formData.endDate))
            : null,
        notes: formData.notes,
        attachments: formData.attachments,
        schedule: (formData.schedule ?? null) as any,
        hasSchedule: formData.hasSchedule ?? false,
      });

      if (projectId) {
        // Save work units if any were created
        if (workUnits.length > 0) {
          try {
            for (const unit of workUnits) {
              // Prepare the work unit schedule based on the form data
              let workUnitSchedule: WorkUnitSchedule | undefined = undefined;

              // Build schedule from work unit-specific settings if enabled
              if (unit.hasSchedule && unit.days.length > 0) {
                workUnitSchedule = {
                  mode: "single",
                  singleSchedule: {
                    allDay: unit.allDay,
                    startTime: unit.allDay ? undefined : unit.startTime,
                    endTime: unit.allDay ? undefined : unit.endTime,
                    days: mapDaysToDayNumbers(unit.days),
                  },
                };
              }

              // Parse pay rate properly, handling 0 as valid value
              const parsedPayRate = parseFloat(unit.payRate);
              const payRateValue = isNaN(parsedPayRate) ? undefined : parsedPayRate;

              // Create the work unit data
              const workUnitData: CreateWorkUnitInput = {
                name: unit.name,
                description: unit.description,
                workersNeeded: unit.workersNeeded,
                englishLevel: mapEnglishLevel(unit.englishLevel),
                payRate: payRateValue,
                schedule: workUnitSchedule,
                startDate: unit.startDate
                  ? Timestamp.fromDate(dateStringToNewYorkDate(unit.startDate))
                  : undefined,
              };

              // Create the work unit in the database
              const workUnitId = await workUnitService.create(projectId, workUnitData);

              // Assign workers to this work unit if any
              for (const worker of unit.workers) {
                const workerAssignment: WorkUnitWorkerAssignment = {
                  workerId: worker.id,
                  workerShifts: worker.workerShifts || null,
                  schedule: worker.schedule,
                };
                await workUnitService.assignWorker(projectId, workUnitId, workerAssignment);
              }
            }
          } catch (workUnitErr) {
            console.error("Failed to create work units:", workUnitErr);
            toast.error("Project created, but some work units failed to save");
          }
        }

        toast.success("Project created successfully");
        navigate({
          to: "/dashboard/admin/projects/$projectId",
          params: { projectId },
        });
      } else {
        navigate({ to: "/dashboard/admin/projects" });
      }
    } catch (err) {
      toast.error("Failed to create project");
      console.error("Failed to create project:", err);
    }
  };

  const isStepComplete = (step: Step): boolean => {
    switch (step) {
      case 1:
        return !!(
          formData.name &&
          formData.customerId &&
          formData.assignedManagerIds &&
          formData.assignedManagerIds.length > 0
        );
      case 2:
        // Validate that endDate is not before startDate
        if (formData.startDate && formData.endDate) {
          const start = new Date(formData.startDate);
          const end = new Date(formData.endDate);
          if (end < start) {
            return false;
          }
        }
        return true;
      case 3:
        return true;
      default:
        return false;
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#F5F7FA]">
      <div className="mx-auto max-w-full">
        {/* Header - responsive layout */}
        {isMobile ? (
          // Mobile: Title + stacked buttons below
          <div className="mb-4">
            <Link
              to="/dashboard/admin/projects"
              className="inline-flex items-center gap-2 text-gray-700 hover:text-gray-900"
            >
              <BackArrow className="h-5 w-5" />
              <span className="text-xl font-semibold text-gray-900">Project details</span>
            </Link>
            <div className="mt-3 flex gap-3">
              <Button
                variant="outline"
                onClick={handleCancel}
                className="border-muted-foreground text-muted-foreground h-10 flex-1 cursor-pointer bg-transparent"
              >
                Cancel
              </Button>
              {currentStep < 3 ? (
                <Button
                  variant="primary"
                  onClick={handleNext}
                  className="h-10 flex-1 cursor-pointer rounded px-6"
                >
                  Next
                  <svg
                    className="ml-1.5 h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={creating}
                  className="h-10 flex-1 cursor-pointer rounded"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="mr-1.5"
                  >
                    <path
                      d="M14.1667 17.5V11.6667C14.1667 11.4457 14.0789 11.2337 13.9226 11.0774C13.7663 10.9211 13.5543 10.8333 13.3333 10.8333H6.66667C6.44565 10.8333 6.23369 10.9211 6.07741 11.0774C5.92113 11.2337 5.83333 11.4457 5.83333 11.6667V17.5M5.83333 2.5V5.83333C5.83333 6.05435 5.92113 6.26631 6.07741 6.42259C6.23369 6.57887 6.44565 6.66667 6.66667 6.66667H12.5M12.6667 2.5C13.1063 2.50626 13.5256 2.68598 13.8333 3L17 6.16667C17.314 6.47438 17.4937 6.89372 17.5 7.33333V15.8333C17.5 16.2754 17.3244 16.6993 17.0118 17.0118C16.6993 17.3244 16.2754 17.5 15.8333 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V4.16667C2.5 3.72464 2.67559 3.30072 2.98816 2.98816C3.30072 2.67559 3.72464 2.5 4.16667 2.5H12.6667Z"
                      stroke="#F8FAFC"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {creating ? "Saving..." : "Save"}
                </Button>
              )}
            </div>
          </div>
        ) : (
          // Tablet & Desktop: Title + buttons in same row
          <div className="mb-4 flex items-center justify-between">
            <Link
              to="/dashboard/admin/projects"
              className="inline-flex items-center gap-2 text-gray-700 hover:text-gray-900"
            >
              <BackArrow className="h-5 w-5" />
              <span className="text-xl font-semibold text-gray-900">Create project</span>
            </Link>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={handleCancel}
                className="border-muted-foreground text-muted-foreground h-10 cursor-pointer bg-transparent"
              >
                Cancel
              </Button>
              {currentStep < 3 ? (
                <Button
                  variant="primary"
                  onClick={handleNext}
                  className="h-10 cursor-pointer rounded px-6"
                >
                  Next
                  <svg
                    className="ml-1.5 h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={creating}
                  className="h-10 cursor-pointer rounded"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="mr-1.5"
                  >
                    <path
                      d="M14.1667 17.5V11.6667C14.1667 11.4457 14.0789 11.2337 13.9226 11.0774C13.7663 10.9211 13.5543 10.8333 13.3333 10.8333H6.66667C6.44565 10.8333 6.23369 10.9211 6.07741 11.0774C5.92113 11.2337 5.83333 11.4457 5.83333 11.6667V17.5M5.83333 2.5V5.83333C5.83333 6.05435 5.92113 6.26631 6.07741 6.42259C6.23369 6.57887 6.44565 6.66667 6.66667 6.66667H12.5M12.6667 2.5C13.1063 2.50626 13.5256 2.68598 13.8333 3L17 6.16667C17.314 6.47438 17.4937 6.89372 17.5 7.33333V15.8333C17.5 16.2754 17.3244 16.6993 17.0118 17.0118C16.6993 17.3244 16.2754 17.5 15.8333 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V4.16667C2.5 3.72464 2.67559 3.30072 2.98816 2.98816C3.30072 2.67559 3.72464 2.5 4.16667 2.5H12.6667Z"
                      stroke="#F8FAFC"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {creating ? "Saving..." : "Save"}
                </Button>
              )}
            </div>
          </div>
        )}

        <div className="mb-4 rounded-lg border border-gray-200 bg-white px-6 py-4">
          <div className="grid grid-cols-3">
            <div className="relative flex flex-col items-center">
              <div
                className={`absolute top-4 left-0 h-0.5 w-1/2 ${currentStep > 1 ? "bg-[#10B981]" : currentStep >= 1 ? "bg-[#7C12C1]" : "bg-gray-200"}`}
              />
              <div
                className={`absolute top-4 right-0 h-0.5 w-1/2 ${currentStep > 1 ? "bg-[#10B981]" : currentStep >= 1 ? "bg-[#7C12C1]" : "bg-gray-200"}`}
              />
              {currentStep > 1 ? (
                <div className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full bg-[#10B981]">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.3337 4L6.00033 11.3333L2.66699 8"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              ) : (
                <div
                  className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full border bg-white text-sm font-medium ${currentStep >= 1 ? "border-[#7C12C1] text-[#7C12C1]" : "border-gray-200 text-gray-400"}`}
                >
                  1
                </div>
              )}
              <span
                className={`mt-2 text-sm ${currentStep === 1 ? "font-medium text-gray-900" : "text-gray-400"}`}
              >
                General
              </span>
            </div>
            <div className="relative flex flex-col items-center">
              <div
                className={`absolute top-4 left-0 h-0.5 w-1/2 ${currentStep > 2 ? "bg-[#10B981]" : currentStep >= 2 ? "bg-[#7C12C1]" : "bg-gray-200"}`}
              />
              <div
                className={`absolute top-4 right-0 h-0.5 w-1/2 ${currentStep > 2 ? "bg-[#10B981]" : currentStep >= 2 ? "bg-[#7C12C1]" : "bg-gray-200"}`}
              />
              {currentStep > 2 ? (
                <div className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full bg-[#10B981]">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.3337 4L6.00033 11.3333L2.66699 8"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              ) : (
                <div
                  className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full border bg-white text-sm font-medium ${currentStep >= 2 ? "border-[#7C12C1] text-[#7C12C1]" : "border-gray-200 text-gray-400"}`}
                >
                  2
                </div>
              )}
              <span
                className={`mt-2 text-sm ${currentStep === 2 ? "font-medium text-gray-900" : "text-gray-400"}`}
              >
                Schedule
              </span>
            </div>
            <div className="relative flex flex-col items-center">
              <div
                className={`absolute top-4 left-0 h-0.5 w-1/2 ${currentStep >= 3 ? "bg-[#7C12C1]" : "bg-gray-200"}`}
              />
              <div
                className={`absolute top-4 right-0 h-0.5 w-1/2 ${currentStep >= 3 ? "bg-[#7C12C1]" : "bg-gray-200"}`}
              />
              <div
                className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full border bg-white text-sm font-medium ${currentStep >= 3 ? "border-[#7C12C1] text-[#7C12C1]" : "border-gray-200 text-gray-400"}`}
              >
                3
              </div>
              <span
                className={`mt-2 text-sm ${currentStep === 3 ? "font-medium text-gray-900" : "text-gray-400"}`}
              >
                Work units
              </span>
            </div>
          </div>
        </div>

        {currentStep === 1 && (
          <div className={`flex flex-col gap-4 ${isDesktop ? "lg:flex-row lg:items-start" : ""}`}>
            <div
              className={`w-full space-y-4 rounded-lg border border-gray-200 bg-white p-4 ${isDesktop ? "lg:w-1/2" : ""}`}
            >
              <div>
                <Label htmlFor="name" className="mb-2">
                  Name<span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  className={`h-10 ${validationErrors.name ? "border-red-500" : ""}`}
                  placeholder="Project name"
                />
                {validationErrors.name && (
                  <p className="mt-1 text-sm text-red-500">Please enter project name</p>
                )}
              </div>
              {/* Status & Type - stacked on mobile, side by side on tablet+ */}
              {isMobile ? (
                <>
                  <div>
                    <Label htmlFor="status" className="mb-2">
                      Status
                    </Label>
                    <SearchableSelectPopover
                      items={projectStatuses}
                      selectedValue={formData.statusId}
                      onSelect={(value) => handleInputChange("statusId", value)}
                      getKey={(status) => status.id}
                      getLabel={(status) => status.name}
                      triggerLabel={
                        formData.statusId
                          ? projectStatuses.find((s) => s.id === formData.statusId)?.name ||
                            "Select status"
                          : "Select status"
                      }
                      renderTriggerLabel={() => {
                        const selectedStatus = projectStatuses.find(
                          (s) => s.id === formData.statusId,
                        );
                        if (selectedStatus) {
                          return (
                            <span className="flex items-center gap-2">
                              <span
                                className="h-2.5 w-2.5 rounded-full"
                                style={{ backgroundColor: selectedStatus.color || "#6b7280" }}
                              />
                              {selectedStatus.name}
                            </span>
                          );
                        }
                        return "Select status";
                      }}
                      renderItem={(status) => (
                        <span className="flex items-center gap-2">
                          <span
                            className="h-2.5 w-2.5 rounded-full"
                            style={{ backgroundColor: status.color || "#6b7280" }}
                          />
                          {status.name}
                        </span>
                      )}
                      placeholder="Search status..."
                      emptyText="No status found"
                      buttonClassName="w-full justify-between"
                      contentClassName="w-full p-0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="type" className="mb-2">
                      Type
                    </Label>
                    <Combobox
                      value={formData.type}
                      onChange={(value) => {
                        handleInputChange("type", value);
                        // When switching to one-time and start date exists, set end date same as start
                        if (value === "one-time" && formData.startDate) {
                          handleInputChange("endDate", formData.startDate);
                        }
                      }}
                      options={[
                        { value: "ongoing", label: "Ongoing" },
                        { value: "one-time", label: "One-time" },
                      ]}
                      placeholder="Select type"
                    />
                  </div>
                </>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="status" className="mb-2">
                      Status
                    </Label>
                    <SearchableSelectPopover
                      items={projectStatuses}
                      selectedValue={formData.statusId}
                      onSelect={(value) => handleInputChange("statusId", value)}
                      getKey={(status) => status.id}
                      getLabel={(status) => status.name}
                      triggerLabel={
                        formData.statusId
                          ? projectStatuses.find((s) => s.id === formData.statusId)?.name ||
                            "Select status"
                          : "Select status"
                      }
                      renderTriggerLabel={() => {
                        const selectedStatus = projectStatuses.find(
                          (s) => s.id === formData.statusId,
                        );
                        if (selectedStatus) {
                          return (
                            <span className="flex items-center gap-2">
                              <span
                                className="h-2.5 w-2.5 rounded-full"
                                style={{ backgroundColor: selectedStatus.color || "#6b7280" }}
                              />
                              {selectedStatus.name}
                            </span>
                          );
                        }
                        return "Select status";
                      }}
                      renderItem={(status) => (
                        <span className="flex items-center gap-2">
                          <span
                            className="h-2.5 w-2.5 rounded-full"
                            style={{ backgroundColor: status.color || "#6b7280" }}
                          />
                          {status.name}
                        </span>
                      )}
                      placeholder="Search status..."
                      emptyText="No status found"
                      buttonClassName="w-full justify-between"
                      contentClassName="w-full p-0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="type" className="mb-2">
                      Type
                    </Label>
                    <Combobox
                      value={formData.type}
                      onChange={(value) => {
                        handleInputChange("type", value);
                        // When switching to one-time and start date exists, set end date same as start
                        if (value === "one-time" && formData.startDate) {
                          handleInputChange("endDate", formData.startDate);
                        }
                      }}
                      options={[
                        { value: "ongoing", label: "Ongoing" },
                        { value: "one-time", label: "One-time" },
                      ]}
                      placeholder="Select type"
                    />
                  </div>
                </div>
              )}
              <div>
                <Label htmlFor="assignedManagerIds" className="mb-2">
                  Managers<span className="text-red-500">*</span>
                </Label>
                <div className="w-full">
                  <MultiSelectCombobox
                    value={formData.assignedManagerIds}
                    onChange={(value) => handleInputChange("assignedManagerIds", value)}
                    options={managerUsers.map((manager) => ({
                      value: manager.uid,
                      label: manager.name || manager.email,
                    }))}
                    placeholder="Select managers"
                    searchPlaceholder="Search managers..."
                  />
                  {validationErrors.assignedManagerIds && (
                    <p className="mt-1 text-sm text-red-500">Please select at least one manager</p>
                  )}
                </div>
              </div>
              <div>
                <Label htmlFor="assignedSupportUserIds" className="mb-2">
                  Support Users
                </Label>
                <div className="w-full">
                  <MultiSelectCombobox
                    value={formData.assignedSupportUserIds}
                    onChange={(value) => handleInputChange("assignedSupportUserIds", value)}
                    options={supportUsers.map((support) => ({
                      value: support.uid,
                      label: support.name || support.email,
                    }))}
                    placeholder="Select support users"
                    searchPlaceholder="Search support users..."
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="assignedAdminId" className="mb-2">
                  Admin
                </Label>
                <div className="w-full">
                  <Combobox
                    value={formData.assignedAdminId || ""}
                    onChange={(value) => handleInputChange("assignedAdminId", value || null)}
                    options={[
                      { value: "", label: "No admin assigned" },
                      ...adminUsers.map((admin) => ({
                        value: admin.uid,
                        label: admin.name || admin.email,
                      })),
                    ]}
                    placeholder="Select admin (optional)"
                  />
                </div>
              </div>
              {/* Start & End date - stacked on mobile, side by side on tablet+ */}
              {isMobile ? (
                <>
                  <div>
                    <Label htmlFor="startDate" className="mb-2">
                      Start date
                    </Label>
                    <div className="relative">
                      <Input
                        id="startDate"
                        type="date"
                        value={formData.startDate}
                        onChange={(e) => handleInputChange("startDate", e.target.value)}
                        className={`h-10 appearance-none bg-white pr-10 [&::-webkit-calendar-picker-indicator]:pointer-events-none [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-inner-spin-button]:hidden ${!formData.startDate ? "[&::-webkit-datetime-edit]:opacity-0 [&::-webkit-datetime-edit-day-field]:opacity-0 [&::-webkit-datetime-edit-fields-wrapper]:opacity-0 [&::-webkit-datetime-edit-month-field]:opacity-0 [&::-webkit-datetime-edit-text]:opacity-0 [&::-webkit-datetime-edit-year-field]:opacity-0" : ""}`}
                      />
                      {!formData.startDate && (
                        <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-gray-500">
                          Select date
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => triggerPicker("startDate")}
                        className={`absolute top-1/2 right-3 -translate-y-1/2 ${formData.startDate ? "text-gray-900" : "text-gray-400 hover:text-gray-600"}`}
                      >
                        <Calendar className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="endDate" className="mb-2">
                      End date
                    </Label>
                    <div className="relative">
                      <Input
                        id="endDate"
                        type="date"
                        value={formData.endDate}
                        onChange={(e) => handleInputChange("endDate", e.target.value)}
                        className={`h-10 appearance-none bg-white pr-10 [&::-webkit-calendar-picker-indicator]:pointer-events-none [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-inner-spin-button]:hidden ${!formData.endDate ? "[&::-webkit-datetime-edit]:opacity-0 [&::-webkit-datetime-edit-day-field]:opacity-0 [&::-webkit-datetime-edit-fields-wrapper]:opacity-0 [&::-webkit-datetime-edit-month-field]:opacity-0 [&::-webkit-datetime-edit-text]:opacity-0 [&::-webkit-datetime-edit-year-field]:opacity-0" : ""}`}
                      />
                      {!formData.endDate && (
                        <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-gray-500">
                          Select date
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => triggerPicker("endDate")}
                        className={`absolute top-1/2 right-3 -translate-y-1/2 ${formData.endDate ? "text-gray-900" : "text-gray-400 hover:text-gray-600"}`}
                      >
                        <Calendar className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="startDate" className="mb-2">
                      Start date
                    </Label>
                    <div className="relative">
                      <Input
                        id="startDate"
                        type="date"
                        value={formData.startDate}
                        onChange={(e) => handleInputChange("startDate", e.target.value)}
                        className={`h-10 appearance-none bg-white pr-10 [&::-webkit-calendar-picker-indicator]:pointer-events-none [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-inner-spin-button]:hidden ${!formData.startDate ? "[&::-webkit-datetime-edit]:opacity-0 [&::-webkit-datetime-edit-day-field]:opacity-0 [&::-webkit-datetime-edit-fields-wrapper]:opacity-0 [&::-webkit-datetime-edit-month-field]:opacity-0 [&::-webkit-datetime-edit-text]:opacity-0 [&::-webkit-datetime-edit-year-field]:opacity-0" : ""}`}
                      />
                      {!formData.startDate && (
                        <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-gray-500">
                          Select date
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => triggerPicker("startDate")}
                        className={`absolute top-1/2 right-3 -translate-y-1/2 ${formData.startDate ? "text-gray-900" : "text-gray-400 hover:text-gray-600"}`}
                      >
                        <Calendar className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="endDate" className="mb-2">
                      End date
                    </Label>
                    <div className="relative">
                      <Input
                        id="endDate"
                        type="date"
                        value={formData.endDate}
                        onChange={(e) => handleInputChange("endDate", e.target.value)}
                        className={`h-10 appearance-none bg-white pr-10 [&::-webkit-calendar-picker-indicator]:pointer-events-none [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-inner-spin-button]:hidden ${!formData.endDate ? "[&::-webkit-datetime-edit]:opacity-0 [&::-webkit-datetime-edit-day-field]:opacity-0 [&::-webkit-datetime-edit-fields-wrapper]:opacity-0 [&::-webkit-datetime-edit-month-field]:opacity-0 [&::-webkit-datetime-edit-text]:opacity-0 [&::-webkit-datetime-edit-year-field]:opacity-0" : ""}`}
                      />
                      {!formData.endDate && (
                        <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-gray-500">
                          Select date
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => triggerPicker("endDate")}
                        className={`absolute top-1/2 right-3 -translate-y-1/2 ${formData.endDate ? "text-gray-900" : "text-gray-400 hover:text-gray-600"}`}
                      >
                        <Calendar className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
              <div>
                <Label htmlFor="customerId" className="mb-2">
                  Customer<span className="text-red-500">*</span>
                </Label>
                <div className="flex gap-3">
                  <div className="min-w-0 flex-1">
                    <Combobox
                      value={formData.customerId}
                      onChange={(value) => {
                        handleInputChange("customerId", value);
                        // Clear contact selection when customer changes
                        handleInputChange("customerContactId", null);
                      }}
                      options={customerUsers.map((customer) => ({
                        value: customer.uid,
                        label: customer.name || customer.email,
                      }))}
                      placeholder="Select customer"
                      error={validationErrors.customerId}
                    />
                    {validationErrors.customerId && (
                      <p className="mt-1 text-sm text-red-500">Please select customer</p>
                    )}
                  </div>
                  <CreateCustomerDialog
                    trigger={
                      isMobile ? (
                        <Button
                          variant="outline"
                          size="icon"
                          className="text-muted-foreground h-[42px] w-[42px] shrink-0"
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 4v16m8-8H4"
                            />
                          </svg>
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-muted-foreground h-[42px] whitespace-nowrap"
                        >
                          <svg
                            className="mr-1.5 h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 4v16m8-8H4"
                            />
                          </svg>
                          Create new
                        </Button>
                      )
                    }
                    onSuccess={handleCustomerCreated}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="customerContactId" className="mb-2">
                  Customer contact
                </Label>
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <Combobox
                      value={formData.customerContactId || ""}
                      onChange={(value) => handleInputChange("customerContactId", value)}
                      options={(() => {
                        const customer = customerUsers.find((u) => u.uid === formData.customerId);
                        return (customer?.contacts || []).map((contact) => ({
                          value: contact.uniqueId || contact.id,
                          label: [
                            contact.title,
                            contact.name,
                            contact.email ? `<${contact.email}>` : null,
                          ]
                            .filter(Boolean)
                            .join(" "),
                        }));
                      })()}
                      placeholder={
                        formData.customerId ? "Select customer contact" : "Select a customer first"
                      }
                      disabled={!formData.customerId}
                    />
                  </div>
                  <Dialog open={isContactDialogOpen} onOpenChange={setIsContactDialogOpen}>
                    <DialogTrigger asChild>
                      {isMobile ? (
                        <Button
                          variant="outline"
                          size="icon"
                          className="text-muted-foreground h-[42px] w-[42px] shrink-0"
                          disabled={!formData.customerId}
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 4v16m8-8H4"
                            />
                          </svg>
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-muted-foreground h-[42px] whitespace-nowrap"
                          disabled={!formData.customerId}
                        >
                          <svg
                            className="mr-1.5 h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 4v16m8-8H4"
                            />
                          </svg>
                          Create new
                        </Button>
                      )}
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add New Contact</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="contact-name">Full Name *</Label>
                          <Input
                            id="contact-name"
                            value={newContactData.name}
                            onChange={(e) =>
                              setNewContactData((prev) => ({ ...prev, name: e.target.value }))
                            }
                            placeholder="Contact person's name"
                          />
                        </div>
                        <div>
                          <Label htmlFor="contact-title">Title</Label>
                          <Input
                            id="contact-title"
                            value={newContactData.title}
                            onChange={(e) =>
                              setNewContactData((prev) => ({ ...prev, title: e.target.value }))
                            }
                            placeholder="Job title (e.g., Manager)"
                          />
                        </div>
                        <div>
                          <Label htmlFor="contact-email">Email</Label>
                          <Input
                            id="contact-email"
                            type="email"
                            value={newContactData.email}
                            onChange={(e) =>
                              setNewContactData((prev) => ({ ...prev, email: e.target.value }))
                            }
                            placeholder="email@example.com"
                          />
                        </div>
                        <div>
                          <Label htmlFor="contact-phone">Phone</Label>
                          <Input
                            id="contact-phone"
                            type="tel"
                            value={newContactData.phone}
                            onChange={(e) =>
                              setNewContactData((prev) => ({ ...prev, phone: e.target.value }))
                            }
                            placeholder="+1 (555) 000-0000"
                          />
                        </div>
                        <div>
                          <Label htmlFor="contact-notes">Notes</Label>
                          <Textarea
                            id="contact-notes"
                            value={newContactData.notes}
                            onChange={(e) =>
                              setNewContactData((prev) => ({ ...prev, notes: e.target.value }))
                            }
                            placeholder="Additional notes about this contact"
                            rows={3}
                          />
                        </div>
                        <div className="flex justify-end gap-3">
                          <Button type="button" variant="outline" onClick={handleContactCancel}>
                            Cancel
                          </Button>
                          <Button
                            type="button"
                            onClick={handleAddNewContact}
                            disabled={contactLoading || !newContactData.name.trim()}
                          >
                            {contactLoading ? "Adding..." : "Add Contact"}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  id="smsNotifications"
                  checked={smsNotifications}
                  onCheckedChange={setSmsNotifications}
                  disabled={!formData.customerId}
                />
                <Label
                  htmlFor="smsNotifications"
                  className={`cursor-pointer ${!formData.customerId ? "opacity-50" : ""}`}
                >
                  SMS Notifications
                </Label>
              </div>
              <div>
                <Label htmlFor="assignedAgencyId" className="mb-2">
                  Agency
                </Label>
                <div className="w-full">
                  <Combobox
                    value={formData.assignedAgencyId || ""}
                    onChange={(value) => handleInputChange("assignedAgencyId", value)}
                    options={agencies.map((agency) => ({
                      value: agency.id,
                      label: agency.name,
                    }))}
                    placeholder="Select agency"
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>Attachments</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-muted-foreground h-9"
                    onClick={() => {
                      const input = document.createElement("input");
                      input.type = "file";
                      input.onchange = (e) => {
                        const file = (e.target as HTMLInputElement).files?.[0];
                        if (file) {
                          setUploadedFiles((prev) => [
                            ...prev,
                            { id: Date.now().toString(), file },
                          ]);
                        }
                      };
                      input.click();
                    }}
                  >
                    <svg
                      className="mr-2 h-5 w-5"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M13.3325 4.99999L6.32088 12.155C6.00825 12.4676 5.83262 12.8916 5.83262 13.3337C5.83262 13.7759 6.00825 14.1999 6.32088 14.5125C6.6335 14.8251 7.05751 15.0008 7.49963 15.0008C7.94174 15.0008 8.36575 14.8251 8.67838 14.5125L15.69 7.3575C16.3152 6.73236 16.6664 5.88449 16.6664 5.00041C16.6664 4.11633 16.3152 3.26847 15.69 2.64333C15.0649 2.01819 14.217 1.66699 13.333 1.66699C12.4489 1.66699 11.601 2.01819 10.9759 2.64333L3.99338 9.76916C3.52287 10.2321 3.14868 10.7835 2.89238 11.3918C2.63607 12 2.50273 12.653 2.50004 13.313C2.49735 13.973 2.62537 14.6271 2.87672 15.2374C3.12806 15.8477 3.49775 16.4022 3.96447 16.8689C4.43118 17.3356 4.98569 17.7053 5.59599 17.9567C6.2063 18.208 6.86033 18.336 7.52036 18.3333C8.18039 18.3306 8.83336 18.1973 9.4416 17.941C10.0498 17.6847 10.6013 17.3105 11.0642 16.84L18.0467 9.71416"
                        stroke="#62748E"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Upload File
                  </Button>
                </div>
                {uploadedFiles.length === 0 ? (
                  <p className="text-muted-foreground mt-2 text-sm">No attachments available</p>
                ) : (
                  <div className="mt-2 space-y-2">
                    {uploadedFiles.map(({ id, file }) => {
                      const fileExtension = file.name.split(".").pop()?.toUpperCase() as any;
                      return (
                        <FileUploadItem
                          key={id}
                          id={id}
                          fileType={fileExtension || "PDF"}
                          fileName={file.name}
                          fileSize={`${(file.size / 1024).toFixed(1)} KB`}
                          state="default"
                          onDownload={() => {
                            const url = URL.createObjectURL(file);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = file.name;
                            a.click();
                            URL.revokeObjectURL(url);
                          }}
                          onDelete={() => {
                            setUploadedFiles((prev) => prev.filter((f) => f.id !== id));
                          }}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <div className={`flex w-full flex-col gap-4 ${isDesktop ? "lg:w-1/2" : ""}`}>
              <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4">
                <div className="mb-2 flex items-center justify-between">
                  <Label htmlFor="jobLocation">Job Location</Label>
                  <div className="flex items-center gap-2">
                    <label htmlFor="useCustomerAddress" className="text-sm text-gray-900">
                      Use customer address
                    </label>
                    <Switch
                      id="useCustomerAddress"
                      checked={useCustomerAddress}
                      disabled={!formData.customerId}
                      onCheckedChange={(checked) =>
                        setUseCustomerAddress(!!formData.customerId && checked)
                      }
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="jobLocation" className="mb-2">
                    Address
                  </Label>
                  <PlacesAutocomplete
                    value={jobLocationAddress}
                    onChange={(value, meta) => {
                      setFormData((prev) => {
                        const existingLocation = isProjectJobLocation(prev.jobLocation)
                          ? prev.jobLocation
                          : null;

                        const updatedLocation: ProjectJobLocation = {
                          address: value,
                          latitude:
                            meta?.coordinates?.latitude ?? existingLocation?.latitude ?? null,
                          longitude:
                            meta?.coordinates?.longitude ?? existingLocation?.longitude ?? null,
                          city: meta?.address?.city ?? existingLocation?.city ?? null,
                          state: meta?.address?.state ?? existingLocation?.state ?? null,
                          zipCode: meta?.address?.zipCode ?? existingLocation?.zipCode ?? null,
                        };

                        if (existingLocation?.addressNotes) {
                          updatedLocation.addressNotes = existingLocation.addressNotes;
                        }

                        return {
                          ...prev,
                          jobLocation: updatedLocation,
                        };
                      });
                    }}
                    placeholder="Search for a location..."
                    types="geocode"
                    language="en"
                    disabled={useCustomerAddress}
                  />
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="jobLocationCity" className="mb-1">
                      City
                    </Label>
                    <Input
                      id="jobLocationCity"
                      placeholder="City"
                      value={jobLocationCity}
                      disabled={useCustomerAddress}
                      onChange={(e) =>
                        setFormData((prev) => {
                          const currentLocation = buildJobLocationObject(prev.jobLocation);
                          return {
                            ...prev,
                            jobLocation: {
                              ...currentLocation,
                              city: e.target.value,
                            },
                          };
                        })
                      }
                      className="h-10"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="jobLocationState" className="mb-1">
                      State
                    </Label>
                    <Input
                      id="jobLocationState"
                      placeholder="State"
                      value={jobLocationState}
                      disabled={useCustomerAddress}
                      onChange={(e) =>
                        setFormData((prev) => {
                          const currentLocation = buildJobLocationObject(prev.jobLocation);
                          return {
                            ...prev,
                            jobLocation: {
                              ...currentLocation,
                              state: e.target.value,
                            },
                          };
                        })
                      }
                      className="h-10"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="jobLocationZip" className="mb-1">
                      ZIP
                    </Label>
                    <Input
                      id="jobLocationZip"
                      placeholder="ZIP code"
                      value={jobLocationZip}
                      disabled={useCustomerAddress}
                      onChange={(e) =>
                        setFormData((prev) => {
                          const currentLocation = buildJobLocationObject(prev.jobLocation);
                          return {
                            ...prev,
                            jobLocation: {
                              ...currentLocation,
                              zipCode: e.target.value,
                            },
                          };
                        })
                      }
                      className="h-10"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="addressDetails" className="mb-2">
                    Address details
                  </Label>
                  <Input
                    id="addressDetails"
                    placeholder="Suite number, door code, which door, etc."
                    value={jobLocationNotes}
                    onChange={(e) =>
                      setFormData((prev) => {
                        const currentLocation = buildJobLocationObject(prev.jobLocation);
                        return {
                          ...prev,
                          jobLocation: {
                            ...currentLocation,
                            addressNotes: e.target.value,
                          },
                        };
                      })
                    }
                    className="h-10"
                  />
                </div>
              </div>
              <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4">
                <div>
                  <Label htmlFor="description" className="mb-2">
                    Description
                  </Label>
                  <RichTextEditor
                    value={formData.description}
                    onChange={(html) => handleInputChange("description", html)}
                    placeholder="Enter project description"
                    minHeight="120px"
                  />
                </div>
                <div>
                  <Label htmlFor="notes" className="mb-2">
                    Notes
                  </Label>
                  <Textarea
                    id="notes"
                    placeholder="Enter additional notes about this project"
                    rows={4}
                    value={formData.notes}
                    onChange={(e) => handleInputChange("notes", e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div className={`w-full ${isDesktop ? "lg:w-1/2" : ""}`}>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="space-y-6">
                <div>
                  <Label className="mb-2 block">Schedule format</Label>
                  <div className="w-full">
                    <SegmentControl
                      tabs={[
                        { label: "Preset", value: "preset" },
                        { label: "Custom", value: "custom" },
                      ]}
                      value={scheduleFormat}
                      onChange={(value) => setScheduleFormat(value as "preset" | "custom")}
                    />
                  </div>
                </div>

                {scheduleFormat === "preset" && (
                  <>
                    <div>
                      <Label className="mb-3 block">Select preset</Label>
                      <div className="space-y-3">
                        <div>
                          <Radio
                            name="preset"
                            value="daily"
                            checked={selectedPreset === "daily"}
                            onChange={(v) => {
                              setSelectedPreset(v);
                              setAllDay(true);
                            }}
                            label="Daily"
                            secondary="Every day"
                          />
                        </div>
                        <div>
                          <Radio
                            name="preset"
                            value="weekly"
                            checked={selectedPreset === "weekly"}
                            onChange={(v) => {
                              setSelectedPreset(v);
                              setAllDay(true);
                            }}
                            label="Weekly"
                            secondary="Once per week"
                          />
                        </div>
                        <div>
                          <Radio
                            name="preset"
                            value="weekdays"
                            checked={selectedPreset === "weekdays"}
                            onChange={(v) => {
                              setSelectedPreset(v);
                              setAllDay(true);
                            }}
                            label="Weekdays"
                            secondary="Monday through Friday"
                          />
                        </div>
                        <div>
                          <Radio
                            name="preset"
                            value="weekends"
                            checked={selectedPreset === "weekends"}
                            onChange={(v) => {
                              setSelectedPreset(v);
                              setAllDay(true);
                            }}
                            label="Weekends"
                            secondary="Saturday and Sunday"
                          />
                        </div>
                        <div>
                          <Radio
                            name="preset"
                            value="monthly"
                            checked={selectedPreset === "monthly"}
                            onChange={(v) => {
                              setSelectedPreset(v);
                              setAllDay(true);
                            }}
                            label="Monthly"
                            secondary="Once per month"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium text-gray-900">Time settings</div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={!!selectedPreset && allDay}
                            onCheckedChange={setAllDay}
                            disabled={!selectedPreset}
                          />
                          <span
                            className={`text-sm ${!selectedPreset ? "text-gray-400" : "text-gray-900"}`}
                          >
                            All Day
                          </span>
                        </div>
                      </div>
                      {selectedPreset && !allDay && (
                        <div className="mt-3 grid grid-cols-2 gap-4">
                          <div>
                            <Label className="mb-2 block">Start time</Label>
                            <div className="relative">
                              <Input
                                id="preset-start-time"
                                type="time"
                                value={presetStartTime}
                                onChange={(e) => setPresetStartTime(e.target.value)}
                                className={`h-10 appearance-none bg-white pr-10 [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-inner-spin-button]:hidden ${!presetStartTime ? "[&::-webkit-datetime-edit]:opacity-0 [&::-webkit-datetime-edit-ampm-field]:opacity-0 [&::-webkit-datetime-edit-fields-wrapper]:opacity-0 [&::-webkit-datetime-edit-hour-field]:opacity-0 [&::-webkit-datetime-edit-minute-field]:opacity-0 [&::-webkit-datetime-edit-second-field]:opacity-0 [&::-webkit-datetime-edit-text]:opacity-0" : ""}`}
                              />
                              {!presetStartTime && (
                                <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-gray-500">
                                  Select time
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() => triggerPicker("preset-start-time")}
                                className={`absolute top-1/2 right-3 -translate-y-1/2 ${presetStartTime ? "text-gray-900" : "text-gray-400 hover:text-gray-600"}`}
                              >
                                <Clock className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          <div>
                            <Label className="mb-2 block">End time</Label>
                            <div className="relative">
                              <Input
                                id="preset-end-time"
                                type="time"
                                value={presetEndTime}
                                onChange={(e) => setPresetEndTime(e.target.value)}
                                className={`h-10 appearance-none bg-white pr-10 [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-inner-spin-button]:hidden ${!presetEndTime ? "[&::-webkit-datetime-edit]:opacity-0 [&::-webkit-datetime-edit-ampm-field]:opacity-0 [&::-webkit-datetime-edit-fields-wrapper]:opacity-0 [&::-webkit-datetime-edit-hour-field]:opacity-0 [&::-webkit-datetime-edit-minute-field]:opacity-0 [&::-webkit-datetime-edit-second-field]:opacity-0 [&::-webkit-datetime-edit-text]:opacity-0" : ""}`}
                              />
                              {!presetEndTime && (
                                <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-gray-500">
                                  Select time
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() => triggerPicker("preset-end-time")}
                                className={`absolute top-1/2 right-3 -translate-y-1/2 ${presetEndTime ? "text-gray-900" : "text-gray-400 hover:text-gray-600"}`}
                              >
                                <Clock className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div>
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium text-gray-900">Schedule boundaries</div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={!!selectedPreset && advanced}
                            onCheckedChange={setAdvanced}
                            disabled={!selectedPreset}
                          />
                          <span
                            className={`text-sm ${!selectedPreset ? "text-gray-400" : "text-gray-900"}`}
                          >
                            Advanced
                          </span>
                        </div>
                      </div>
                      {selectedPreset && advanced && (
                        <div className="mt-3 grid grid-cols-2 gap-4">
                          <div>
                            <Label className="mb-2 block">Start date</Label>
                            <div className="relative">
                              <Input
                                id="preset-boundary-start-date"
                                type="date"
                                value={presetBoundaryStartDate}
                                onChange={(e) => setPresetBoundaryStartDate(e.target.value)}
                                className={`h-10 appearance-none bg-white pr-10 [&::-webkit-calendar-picker-indicator]:pointer-events-none [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-inner-spin-button]:hidden ${!presetBoundaryStartDate ? "[&::-webkit-datetime-edit]:opacity-0 [&::-webkit-datetime-edit-day-field]:opacity-0 [&::-webkit-datetime-edit-fields-wrapper]:opacity-0 [&::-webkit-datetime-edit-month-field]:opacity-0 [&::-webkit-datetime-edit-text]:opacity-0 [&::-webkit-datetime-edit-year-field]:opacity-0" : ""}`}
                              />
                              {!presetBoundaryStartDate && (
                                <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-gray-500">
                                  Select date
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() => triggerPicker("preset-boundary-start-date")}
                                className={`absolute top-1/2 right-3 -translate-y-1/2 ${presetBoundaryStartDate ? "text-gray-900" : "text-gray-400 hover:text-gray-600"}`}
                              >
                                <Calendar className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          <div>
                            <Label className="mb-2 block">End date</Label>
                            <div className="relative">
                              <Input
                                id="preset-boundary-end-date"
                                type="date"
                                value={presetBoundaryEndDate}
                                onChange={(e) => setPresetBoundaryEndDate(e.target.value)}
                                className={`h-10 appearance-none bg-white pr-10 [&::-webkit-calendar-picker-indicator]:pointer-events-none [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-inner-spin-button]:hidden ${!presetBoundaryEndDate ? "[&::-webkit-datetime-edit]:opacity-0 [&::-webkit-datetime-edit-day-field]:opacity-0 [&::-webkit-datetime-edit-fields-wrapper]:opacity-0 [&::-webkit-datetime-edit-month-field]:opacity-0 [&::-webkit-datetime-edit-text]:opacity-0 [&::-webkit-datetime-edit-year-field]:opacity-0" : ""}`}
                              />
                              {!presetBoundaryEndDate && (
                                <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-gray-500">
                                  Select date
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() => triggerPicker("preset-boundary-end-date")}
                                className={`absolute top-1/2 right-3 -translate-y-1/2 ${presetBoundaryEndDate ? "text-gray-900" : "text-gray-400 hover:text-gray-600"}`}
                              >
                                <Calendar className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div>
                      <Label className="mb-3 block">Next scheduled dates</Label>
                      {!selectedPreset ? (
                        <div
                          className="flex items-start gap-2 rounded-md p-3"
                          style={{ backgroundColor: "#F3F4F6" }}
                        >
                          <svg
                            className="mt-0.5 h-5 w-5 shrink-0 text-gray-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <p className="text-sm text-gray-600">
                            To see next scheduled dates, select a preset first
                          </p>
                        </div>
                      ) : (
                        <div
                          className="rounded-md p-3 text-sm text-gray-800"
                          style={{ backgroundColor: "#F3F4F6" }}
                        >
                          <ul className="list-disc space-y-1 pl-6">
                            {previewDates.map((date, index) => (
                              <li key={index}>
                                {format(date, "EEEE, MMMM d, yyyy")}
                                {selectedPreset && !allDay && (
                                  <span className="ml-1 text-gray-500">
                                    {formatTimeTo12Hour(presetStartTime)} -{" "}
                                    {formatTimeTo12Hour(presetEndTime)}
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                          {previewDates.length === 5 && (
                            <p className="mt-2 text-gray-500">Showing next 5 occurrences...</p>
                          )}
                          {previewDates.length === 0 && (
                            <p className="text-gray-500">No dates scheduled</p>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}

                {scheduleFormat === "custom" && (
                  <>
                    {/* Frequency and Every row - stacked on mobile, side by side on tablet+ */}
                    {isMobile ? (
                      <>
                        <div>
                          <Label className="mb-2 block">Frequency</Label>
                          <Combobox
                            value={customFrequency}
                            onChange={(value) => setCustomFrequency(value as "weekly" | "monthly")}
                            options={[
                              { value: "weekly", label: "Weekly" },
                              { value: "monthly", label: "Monthly" },
                            ]}
                            placeholder="Select frequency"
                          />
                        </div>
                        <div>
                          <Label className="mb-2 block">Every</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="1"
                              value={customEvery}
                              onChange={(e) => setCustomEvery(e.target.value)}
                              className="h-10 flex-1"
                            />
                            <span className="text-sm text-gray-500">
                              {customFrequency === "weekly" ? "week(s)" : "month(s)"}
                            </span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="mb-2 block">Frequency</Label>
                          <Combobox
                            value={customFrequency}
                            onChange={(value) => setCustomFrequency(value as "weekly" | "monthly")}
                            options={[
                              { value: "weekly", label: "Weekly" },
                              { value: "monthly", label: "Monthly" },
                            ]}
                            placeholder="Select frequency"
                          />
                        </div>
                        <div>
                          <Label className="mb-2 block">Every</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="1"
                              value={customEvery}
                              onChange={(e) => setCustomEvery(e.target.value)}
                              className="h-10 w-full"
                            />
                            <span className="text-sm text-gray-500">
                              {customFrequency === "weekly" ? "week(s)" : "month(s)"}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Weekly: Days of the Week - 2 columns on mobile, single row on tablet+ */}
                    {customFrequency === "weekly" && (
                      <div>
                        <Label className="mb-3 block">Days of the Week</Label>
                        {isMobile ? (
                          <div className="grid grid-cols-2 gap-3">
                            {[
                              { value: "sun", label: "Sun" },
                              { value: "thu", label: "Thu" },
                              { value: "mon", label: "Mon" },
                              { value: "fri", label: "Fri" },
                              { value: "tue", label: "Tue" },
                              { value: "sat", label: "Sat" },
                              { value: "wed", label: "Wed" },
                            ].map((day) => (
                              <Checkbox
                                key={day.value}
                                checked={selectedDays.includes(day.value)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedDays([...selectedDays, day.value]);
                                  } else {
                                    setSelectedDays(selectedDays.filter((d) => d !== day.value));
                                  }
                                }}
                                label={day.label}
                              />
                            ))}
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-4">
                            {[
                              { value: "sun", label: "Sun" },
                              { value: "mon", label: "Mon" },
                              { value: "tue", label: "Tue" },
                              { value: "wed", label: "Wed" },
                              { value: "thu", label: "Thu" },
                              { value: "fri", label: "Fri" },
                              { value: "sat", label: "Sat" },
                            ].map((day) => (
                              <Checkbox
                                key={day.value}
                                checked={selectedDays.includes(day.value)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedDays([...selectedDays, day.value]);
                                  } else {
                                    setSelectedDays(selectedDays.filter((d) => d !== day.value));
                                  }
                                }}
                                label={day.label}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Monthly: Schedule type selector */}
                    {customFrequency === "monthly" && (
                      <>
                        <div>
                          <Label className="mb-2 block text-sm font-medium text-gray-900">
                            Monthly Schedule
                          </Label>
                          <div className="w-full">
                            <SegmentControl
                              tabs={[
                                { label: "By date", value: "by-date" },
                                { label: "By day", value: "by-day" },
                              ]}
                              value={monthlyScheduleType}
                              onChange={(value) =>
                                setMonthlyScheduleType(value as "by-date" | "by-day")
                              }
                            />
                          </div>
                        </div>

                        {/* By date: Day of month selector */}
                        {monthlyScheduleType === "by-date" && (
                          <div>
                            <Label className="mb-2 block">Day of month</Label>
                            <Combobox
                              value={dayOfMonth}
                              onChange={setDayOfMonth}
                              options={Array.from({ length: 31 }, (_, i) => ({
                                value: String(i + 1),
                                label: String(i + 1),
                              }))}
                              placeholder="Select day"
                            />
                          </div>
                        )}

                        {/* By day: Week and Day selectors */}
                        {monthlyScheduleType === "by-day" && (
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="mb-2 block">Week</Label>
                              <Combobox
                                value={monthlyWeek}
                                onChange={setMonthlyWeek}
                                options={[
                                  { value: "first", label: "First" },
                                  { value: "second", label: "Second" },
                                  { value: "third", label: "Third" },
                                  { value: "fourth", label: "Fourth" },
                                  { value: "last", label: "Last" },
                                ]}
                                placeholder="Select week"
                              />
                            </div>
                            <div>
                              <Label className="mb-2 block">Day</Label>
                              <Combobox
                                value={monthlyDay}
                                onChange={setMonthlyDay}
                                options={[
                                  { value: "sunday", label: "Sunday" },
                                  { value: "monday", label: "Monday" },
                                  { value: "tuesday", label: "Tuesday" },
                                  { value: "wednesday", label: "Wednesday" },
                                  { value: "thursday", label: "Thursday" },
                                  { value: "friday", label: "Friday" },
                                  { value: "saturday", label: "Saturday" },
                                ]}
                                placeholder="Select day"
                              />
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {/* Time settings */}
                    <div>
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium text-gray-900">Time settings</div>
                        <div className="flex items-center gap-2">
                          <Switch checked={customAllDay} onCheckedChange={setCustomAllDay} />
                          <span className="text-sm text-gray-900">All Day</span>
                        </div>
                      </div>
                      {!customAllDay && (
                        <div className="mt-3 grid grid-cols-2 gap-4">
                          <div>
                            <Label className="mb-2 block">Start time</Label>
                            <div className="relative">
                              <Input
                                id="custom-start-time"
                                type="time"
                                value={customStartTime}
                                onChange={(e) => setCustomStartTime(e.target.value)}
                                className={`h-10 appearance-none bg-white pr-10 [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-inner-spin-button]:hidden ${!customStartTime ? "[&::-webkit-datetime-edit]:opacity-0 [&::-webkit-datetime-edit-ampm-field]:opacity-0 [&::-webkit-datetime-edit-fields-wrapper]:opacity-0 [&::-webkit-datetime-edit-hour-field]:opacity-0 [&::-webkit-datetime-edit-minute-field]:opacity-0 [&::-webkit-datetime-edit-second-field]:opacity-0 [&::-webkit-datetime-edit-text]:opacity-0" : ""}`}
                              />
                              {!customStartTime && (
                                <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-gray-500">
                                  Select time
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() => triggerPicker("custom-start-time")}
                                className={`absolute top-1/2 right-3 -translate-y-1/2 ${customStartTime ? "text-gray-900" : "text-gray-400 hover:text-gray-600"}`}
                              >
                                <Clock className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          <div>
                            <Label className="mb-2 block">End time</Label>
                            <div className="relative">
                              <Input
                                id="custom-end-time"
                                type="time"
                                value={customEndTime}
                                onChange={(e) => setCustomEndTime(e.target.value)}
                                className={`h-10 appearance-none bg-white pr-10 [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-inner-spin-button]:hidden ${!customEndTime ? "[&::-webkit-datetime-edit]:opacity-0 [&::-webkit-datetime-edit-ampm-field]:opacity-0 [&::-webkit-datetime-edit-fields-wrapper]:opacity-0 [&::-webkit-datetime-edit-hour-field]:opacity-0 [&::-webkit-datetime-edit-minute-field]:opacity-0 [&::-webkit-datetime-edit-second-field]:opacity-0 [&::-webkit-datetime-edit-text]:opacity-0" : ""}`}
                              />
                              {!customEndTime && (
                                <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-gray-500">
                                  Select time
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() => triggerPicker("custom-end-time")}
                                className={`absolute top-1/2 right-3 -translate-y-1/2 ${customEndTime ? "text-gray-900" : "text-gray-400 hover:text-gray-600"}`}
                              >
                                <Clock className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Schedule boundaries */}
                    <div>
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium text-gray-900">Schedule boundaries</div>
                        <div className="flex items-center gap-2">
                          <Switch checked={customAdvanced} onCheckedChange={setCustomAdvanced} />
                          <span className="text-sm text-gray-900">Advanced</span>
                        </div>
                      </div>
                      {customAdvanced && (
                        <div className="mt-3 grid grid-cols-2 gap-4">
                          <div>
                            <Label className="mb-2 block">Start date</Label>
                            <div className="relative">
                              <Input
                                id="custom-boundary-start-date"
                                type="date"
                                value={customBoundaryStartDate}
                                onChange={(e) => setCustomBoundaryStartDate(e.target.value)}
                                className={`h-10 appearance-none bg-white pr-10 [&::-webkit-calendar-picker-indicator]:pointer-events-none [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-inner-spin-button]:hidden ${!customBoundaryStartDate ? "[&::-webkit-datetime-edit]:opacity-0 [&::-webkit-datetime-edit-day-field]:opacity-0 [&::-webkit-datetime-edit-fields-wrapper]:opacity-0 [&::-webkit-datetime-edit-month-field]:opacity-0 [&::-webkit-datetime-edit-text]:opacity-0 [&::-webkit-datetime-edit-year-field]:opacity-0" : ""}`}
                              />
                              {!customBoundaryStartDate && (
                                <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-gray-500">
                                  Select date
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() => triggerPicker("custom-boundary-start-date")}
                                className={`absolute top-1/2 right-3 -translate-y-1/2 ${customBoundaryStartDate ? "text-gray-900" : "text-gray-400 hover:text-gray-600"}`}
                              >
                                <Calendar className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          <div>
                            <Label className="mb-2 block">End date</Label>
                            <div className="relative">
                              <Input
                                id="custom-boundary-end-date"
                                type="date"
                                value={customBoundaryEndDate}
                                onChange={(e) => setCustomBoundaryEndDate(e.target.value)}
                                className={`h-10 appearance-none bg-white pr-10 [&::-webkit-calendar-picker-indicator]:pointer-events-none [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-inner-spin-button]:hidden ${!customBoundaryEndDate ? "[&::-webkit-datetime-edit]:opacity-0 [&::-webkit-datetime-edit-day-field]:opacity-0 [&::-webkit-datetime-edit-fields-wrapper]:opacity-0 [&::-webkit-datetime-edit-month-field]:opacity-0 [&::-webkit-datetime-edit-text]:opacity-0 [&::-webkit-datetime-edit-year-field]:opacity-0" : ""}`}
                              />
                              {!customBoundaryEndDate && (
                                <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-gray-500">
                                  Select date
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() => triggerPicker("custom-boundary-end-date")}
                                className={`absolute top-1/2 right-3 -translate-y-1/2 ${customBoundaryEndDate ? "text-gray-900" : "text-gray-400 hover:text-gray-600"}`}
                              >
                                <Calendar className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Next scheduled dates */}
                    <div>
                      <Label className="mb-3 block">Next scheduled dates</Label>
                      {previewDates.length > 0 && (
                        <div
                          className="rounded-md p-3 text-sm text-gray-800"
                          style={{ backgroundColor: "#F3F4F6" }}
                        >
                          <ul className="list-disc space-y-1 pl-6">
                            {previewDates.map((date, index) => (
                              <li key={index}>
                                {format(date, "EEEE, MMMM d, yyyy")}
                                {!customAllDay && (
                                  <span className="ml-1 text-gray-500">
                                    {formatTimeTo12Hour(customStartTime)} -{" "}
                                    {formatTimeTo12Hour(customEndTime)}
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                          {previewDates.length === 5 && (
                            <p className="mt-2 text-gray-500">Showing next 5 occurrences...</p>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {currentStep === 3 && (
          <div className="flex min-h-[calc(100vh-220px)] flex-col rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900">Work units list</h3>
              <Button
                variant="outline"
                size="sm"
                className="h-9 border-purple-600 text-purple-600 hover:bg-purple-600/5"
                onClick={() => setWorkUnitDialogOpen(true)}
              >
                <svg
                  className="mr-1.5 h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add work unit
              </Button>
            </div>

            {/* Empty state */}
            {workUnits.length === 0 && (
              <div className="flex flex-1 flex-col items-center justify-center">
                <svg
                  width="80"
                  height="80"
                  viewBox="0 0 80 80"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="40" cy="40" r="40" fill="#EDE9FE" />
                  <path
                    d="M33.75 46.25V27.5H52.5V38.75L48.75 45L45 47.5H40L33.75 46.25Z"
                    fill="white"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M24.9389 32.1093C24.563 30.491 24.2613 28.6756 24.0337 26.6631C23.8098 24.6823 23.677 22.6808 23.6353 20.6586C23.4725 12.7656 29.7391 6.23511 37.6321 6.07234C37.9343 6.0661 38.2367 6.06946 38.5387 6.0824C39.5186 6.12438 42.9956 6.0824 44.02 6.0824C50.3711 6.0824 55.7925 10.6716 56.8413 16.9355C57.3805 20.1562 57.5516 23.1586 57.3544 25.9428C57.1303 29.1092 57.1303 30.1475 55.993 32.906C55.7249 33.5564 25.4724 34.4057 24.9389 32.1093Z"
                    fill="white"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M40.6282 44.1666C42.7445 44.2245 45.2803 44.3724 48.2356 44.6104L49.7197 47.0885L50.9991 48.7472C50.8547 49.3914 50.6746 49.9647 50.4589 50.4674C49.1122 53.6059 45.187 55.8768 40.5546 55.8768C40.5079 55.8768 40.4613 55.8766 40.4148 55.8761C41.1785 54.6731 41.6601 52.8844 41.8605 50.5098C42.7485 50.1628 43.691 49.7733 44.6881 49.3413C46.2879 48.6481 47.6099 47.5773 48.6542 46.1289L41.2224 45.1779L40.6282 44.1666ZM37.738 44.1489C36.0329 45.1052 34.1717 45.8075 32.2031 46.2059C32.3943 45.8793 32.5438 45.5664 32.6522 45.2665C32.8944 44.5959 34.5294 44.2244 37.5574 44.152L37.738 44.1489Z"
                    fill="white"
                  />
                  <path
                    d="M31.8866 44.498V45.4877C31.8866 47.7335 30.2471 49.643 28.0271 49.9827L21.7336 50.9458C17.2245 51.6358 13.4841 54.7979 12.0535 59.1293C11.7568 60.0273 11.489 60.8551 11.25 61.6127"
                    stroke="#7F22FE"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M49.1447 44.498V45.4877C49.1447 47.7335 50.7842 49.643 53.0042 49.9827L59.2977 50.9458C63.8067 51.6358 67.5471 54.7979 68.9778 59.1293C69.2744 60.0273 69.5422 60.8551 69.7812 61.6127"
                    stroke="#7F22FE"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M44.6336 55.4536C43.4511 55.733 42.1723 55.8856 40.8376 55.8856C35.9676 55.8856 31.8411 53.8534 30.4253 51.0447C30.1985 50.5949 30.0093 50.0818 29.8574 49.5054M49.1453 53.4444C48.5148 53.9141 47.7883 54.3298 46.9852 54.6789L49.1453 53.4444Z"
                    stroke="#7F22FE"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M45 6.25H41.25L32.5 7.5L26.25 12.5L22.5 20V26.875L25 31.25H28.75V38.75L31.25 43.75L35 46.25L40 47.5H42.5V47.3898C37.3361 45.0198 33.75 39.8038 33.75 33.75V21.25C33.75 14.2606 38.5304 8.38771 45 6.72256V6.25Z"
                    fill="#EDE9FE"
                  />
                  <path
                    d="M40.0627 6.875H41.1905C52.3071 6.875 60.2018 17.7029 56.8019 28.2868L55.8418 31.2758C55.6474 31.8809 56.0988 32.5 56.7344 32.5C57.1417 32.5 57.5024 32.237 57.627 31.8492L58.5871 28.8603C62.3757 17.066 53.5783 5 41.1905 5H40.0627C27.6749 5 18.8775 17.066 22.6661 28.8603L23.6262 31.8492C23.7508 32.237 24.1115 32.5 24.5188 32.5C25.1544 32.5 25.6058 31.8809 25.4114 31.2758L24.4513 28.2868C21.0515 17.7029 28.9461 6.875 40.0627 6.875Z"
                    fill="#7F22FE"
                  />
                  <path
                    d="M43.6279 18.5547C45.902 21.2883 49.3931 23.5822 53 24.3672V35.625C53 42.4595 47.4595 48 40.625 48C33.7905 48 28.25 42.4595 28.25 35.625V25.6592C34.1854 24.8241 39.1956 22.0529 43.6279 18.5547Z"
                    stroke="#7F22FE"
                    strokeWidth="1.5"
                  />
                </svg>
                <h3 className="mt-4 text-lg font-semibold text-gray-900">No work units</h3>
                <p className="mt-1 text-sm text-gray-500">Create now or later</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4 h-9 border-purple-600 text-purple-600 hover:bg-purple-600/5"
                  onClick={() => setWorkUnitDialogOpen(true)}
                >
                  <svg
                    className="mr-1.5 h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Add work unit
                </Button>
              </div>
            )}

            {/* Work units list */}
            {workUnits.length > 0 && (
              <div className="mt-4 space-y-3">
                {workUnits.map((unit) => (
                  <div key={unit.id} className="rounded-lg border border-gray-200">
                    {/* Header row - different layout for mobile vs tablet/desktop */}
                    {isMobile ? (
                      <div className="p-3">
                        {/* Row 1: Chevron, Name, Badge */}
                        <div
                          className="flex cursor-pointer items-center justify-between"
                          onClick={() => {
                            setWorkUnits(
                              workUnits.map((u) =>
                                u.id === unit.id ? { ...u, expanded: !u.expanded } : u,
                              ),
                            );
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <svg
                              className={`h-5 w-5 text-gray-500 transition-transform ${unit.expanded ? "rotate-180" : ""}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                            <span className="font-medium text-gray-900">{unit.name}</span>
                          </div>
                          <span
                            className={`flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium ${unit.workers.length >= unit.workersNeeded ? "border-green-200 bg-green-50 text-green-500" : unit.workers.length === 0 ? "text-muted-foreground border-gray-200 bg-gray-50" : "border-red-200 bg-red-50 text-red-500"}`}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M8.9987 5.3335H6.9987M9.9987 1.3335L9.33203 2.66683H11.332C11.6857 2.66683 12.0248 2.80731 12.2748 3.05735C12.5249 3.3074 12.6654 3.64654 12.6654 4.00016V13.3335C12.6654 13.6871 12.5249 14.0263 12.2748 14.2763C12.0248 14.5264 11.6857 14.6668 11.332 14.6668H4.66536C4.31174 14.6668 3.9726 14.5264 3.72256 14.2763C3.47251 14.0263 3.33203 13.6871 3.33203 13.3335V4.00016C3.33203 3.64654 3.47251 3.3074 3.72256 3.05735C3.9726 2.80731 4.31174 2.66683 4.66536 2.66683H6.66536M11.2647 14.6669C11.1114 13.9137 10.7024 13.2367 10.1072 12.7504C9.51196 12.2642 8.76696 11.9985 7.99835 11.9985C7.22973 11.9985 6.48473 12.2642 5.88949 12.7504C5.29425 13.2367 4.88535 13.9137 4.73201 14.6669M5.9987 1.3335L7.9987 5.3335M9.9987 10.0002C9.9987 11.1047 9.10327 12.0002 7.9987 12.0002C6.89413 12.0002 5.9987 11.1047 5.9987 10.0002C5.9987 8.89559 6.89413 8.00016 7.9987 8.00016C9.10327 8.00016 9.9987 8.89559 9.9987 10.0002Z"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            {unit.workers.length}/{unit.workersNeeded}
                          </span>
                        </div>
                        {/* Row 2: Details */}
                        <div className="mt-1 pl-7 text-sm text-gray-500">
                          {unit.englishLevel === "no-requirement"
                            ? "No requirement"
                            : unit.englishLevel}{" "}
                          • ${unit.payRate}/hr • {unit.startDate || "No date"}
                        </div>
                        {/* Row 3: Edit button + Delete icon */}
                        <div className="mt-3 flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-9 flex-1 border-gray-300 text-gray-600"
                          >
                            <svg
                              className="mr-1.5 h-4 w-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                              />
                            </svg>
                            Edit
                          </Button>
                          <button
                            type="button"
                            onClick={() => setWorkUnits(workUnits.filter((u) => u.id !== unit.id))}
                            className="flex h-9 w-9 items-center justify-center rounded border border-red-200 text-red-500 hover:bg-red-50"
                          >
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M8.33333 9.1665V14.1665M11.6667 9.1665V14.1665M15.8333 4.9998V16.6665C15.8333 17.1085 15.6577 17.5325 15.3452 17.845C15.0326 18.1576 14.6087 18.3332 14.1667 18.3332H5.83333C5.39131 18.3332 4.96738 18.1576 4.65482 17.845C4.34226 17.5325 4.16667 17.1085 4.16667 16.6665V4.9998M2.5 4.9998H17.5M6.66667 4.9998V3.3332C6.66667 2.89117 6.84226 2.46724 7.15482 2.15468C7.46738 1.84212 7.89131 1.6665 8.33333 1.6665H11.6667C12.1087 1.6665 12.5326 1.84212 12.8452 2.15468C13.1577 2.46724 13.3333 2.89117 13.3333 3.3332V4.9998"
                                stroke="#FB2C36"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between p-3">
                        <div
                          className="flex flex-1 cursor-pointer items-center gap-3"
                          onClick={() => {
                            setWorkUnits(
                              workUnits.map((u) =>
                                u.id === unit.id ? { ...u, expanded: !u.expanded } : u,
                              ),
                            );
                          }}
                        >
                          <svg
                            className={`h-5 w-5 text-gray-500 transition-transform ${unit.expanded ? "rotate-180" : ""}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                          <span className="font-medium text-gray-900">{unit.name}</span>
                          <span
                            className={`flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium ${unit.workers.length >= unit.workersNeeded ? "border-green-200 bg-green-50 text-green-500" : unit.workers.length === 0 ? "text-muted-foreground border-gray-200 bg-gray-50" : "border-red-200 bg-red-50 text-red-500"}`}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M8.9987 5.3335H6.9987M9.9987 1.3335L9.33203 2.66683H11.332C11.6857 2.66683 12.0248 2.80731 12.2748 3.05735C12.5249 3.3074 12.6654 3.64654 12.6654 4.00016V13.3335C12.6654 13.6871 12.5249 14.0263 12.2748 14.2763C12.0248 14.5264 11.6857 14.6668 11.332 14.6668H4.66536C4.31174 14.6668 3.9726 14.5264 3.72256 14.2763C3.47251 14.0263 3.33203 13.6871 3.33203 13.3335V4.00016C3.33203 3.64654 3.47251 3.3074 3.72256 3.05735C3.9726 2.80731 4.31174 2.66683 4.66536 2.66683H6.66536M11.2647 14.6669C11.1114 13.9137 10.7024 13.2367 10.1072 12.7504C9.51196 12.2642 8.76696 11.9985 7.99835 11.9985C7.22973 11.9985 6.48473 12.2642 5.88949 12.7504C5.29425 13.2367 4.88535 13.9137 4.73201 14.6669M5.9987 1.3335L7.9987 5.3335M9.9987 10.0002C9.9987 11.1047 9.10327 12.0002 7.9987 12.0002C6.89413 12.0002 5.9987 11.1047 5.9987 10.0002C5.9987 8.89559 6.89413 8.00016 7.9987 8.00016C9.10327 8.00016 9.9987 8.89559 9.9987 10.0002Z"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            {unit.workers.length}/{unit.workersNeeded}
                          </span>
                          <span className="text-sm text-gray-500">
                            {unit.englishLevel === "no-requirement"
                              ? "No requirement"
                              : unit.englishLevel}{" "}
                            • ${unit.payRate}/hr • {unit.startDate || "No date"}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            className="flex h-10 w-10 items-center justify-center rounded hover:bg-gray-100"
                          >
                            <svg
                              width="36"
                              height="36"
                              viewBox="0 0 36 36"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <g clipPath="url(#clip0_edit)">
                                <path
                                  d="M18.8346 25.4998H25.5013M20.5013 12.1664L23.8346 15.4998M25.6462 13.6763C26.0868 13.2359 26.3344 12.6384 26.3345 12.0154C26.3346 11.3924 26.0871 10.7948 25.6467 10.3542C25.2062 9.91366 24.6087 9.66609 23.9857 9.66602C23.3627 9.66594 22.7652 9.91335 22.3246 10.3538L11.2029 21.478C11.0094 21.6709 10.8663 21.9084 10.7862 22.1697L9.68541 25.7963C9.66388 25.8684 9.66225 25.945 9.68071 26.0179C9.69916 26.0908 9.73701 26.1574 9.79024 26.2105C9.84347 26.2636 9.9101 26.3014 9.98305 26.3197C10.056 26.3381 10.1325 26.3363 10.2046 26.3147L13.8321 25.2147C14.0931 25.1353 14.3306 24.9931 14.5237 24.8005L25.6462 13.6763Z"
                                  stroke="#62748E"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </g>
                              <defs>
                                <clipPath id="clip0_edit">
                                  <rect
                                    width="20"
                                    height="20"
                                    fill="white"
                                    transform="translate(8 8)"
                                  />
                                </clipPath>
                              </defs>
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => setWorkUnits(workUnits.filter((u) => u.id !== unit.id))}
                            className="flex h-10 w-10 items-center justify-center rounded hover:bg-red-50"
                          >
                            <svg
                              width="36"
                              height="36"
                              viewBox="0 0 36 36"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M16.3333 17.1665V22.1665M19.6667 17.1665V22.1665M23.8333 12.9998V24.6665C23.8333 25.1085 23.6577 25.5325 23.3452 25.845C23.0326 26.1576 22.6087 26.3332 22.1667 26.3332H13.8333C13.3913 26.3332 12.9674 26.1576 12.6548 25.845C12.3423 25.5325 12.1667 25.1085 12.1667 24.6665V12.9998M10.5 12.9998H25.5M14.6667 12.9998V11.3332C14.6667 10.8911 14.8423 10.4672 15.1548 10.1547C15.4674 9.8421 15.8913 9.6665 16.3333 9.6665H19.6667C20.1087 9.6665 20.5326 9.8421 20.8452 10.1547C21.1577 10.4672 21.3333 10.8911 21.3333 11.3332V12.9998"
                                stroke="#FB2C36"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Expanded content */}
                    {unit.expanded && (
                      <div className="border-t border-gray-200 p-4">
                        {/* Description and Add Worker button - stacked on mobile, inline on tablet+ */}
                        {isMobile ? (
                          <div className="mb-4">
                            <p className="mb-3 text-sm text-gray-600">{unit.description || ""}</p>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-9 w-full border-gray-300 text-gray-700"
                              onClick={() => {
                                setAddWorkerToUnitId(unit.id);
                                setAddWorkerDialogOpen(true);
                              }}
                            >
                              <svg
                                className="mr-1.5 h-4 w-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 4v16m8-8H4"
                                />
                              </svg>
                              Add Worker
                            </Button>
                          </div>
                        ) : (
                          <div className="mb-4 flex items-start justify-between">
                            <p className="text-sm text-gray-600">{unit.description || ""}</p>
                            <Button
                              variant="outline"
                              size="sm"
                              className="ml-4 h-8 shrink-0 border-gray-300 text-gray-700"
                              onClick={() => {
                                setAddWorkerToUnitId(unit.id);
                                setAddWorkerDialogOpen(true);
                              }}
                            >
                              <svg
                                className="mr-1.5 h-4 w-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 4v16m8-8H4"
                                />
                              </svg>
                              Add Worker
                            </Button>
                          </div>
                        )}

                        {/* Scheduled dates */}
                        <div className="mb-4">
                          <p className="mb-2 text-sm font-medium text-gray-500">Scheduled dates</p>
                          <div className="rounded-md p-3" style={{ backgroundColor: "#F3F4F6" }}>
                            <ul className="space-y-1 text-sm text-gray-600">
                              <li>• Tuesday, December 4, 2025</li>
                              <li>• Tuesday, December 10, 2025</li>
                              <li>• Tuesday, December 17, 2025</li>
                              <li>• Tuesday, December 24, 2025</li>
                            </ul>
                            <p className="mt-1 text-sm text-gray-500">and 5 more occurrences..</p>
                          </div>
                        </div>

                        {/* Workers list */}
                        {unit.workers.length > 0 && (
                          <div className="space-y-2">
                            {unit.workers.map((worker) => (
                              <div
                                key={worker.id}
                                className="flex items-center justify-between rounded-lg border border-gray-200 p-3"
                              >
                                <div
                                  className={`flex items-center gap-3 ${isMobile ? "flex-wrap" : ""}`}
                                >
                                  <Avatar
                                    src={worker.avatar}
                                    alt={worker.name}
                                    initials={worker.initials}
                                    className="h-10 w-10"
                                  />
                                  <div
                                    className={
                                      isMobile ? "flex flex-col" : "flex items-center gap-3"
                                    }
                                  >
                                    <span className="font-medium text-gray-900">{worker.name}</span>
                                    <div className="flex items-center gap-1 text-sm text-gray-500">
                                      <svg
                                        className="h-4 w-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                        />
                                      </svg>
                                      {worker.phone}
                                    </div>
                                  </div>
                                </div>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      type="button"
                                      className="rounded-lg bg-gray-100 p-2 text-gray-500 hover:bg-gray-200"
                                    >
                                      <svg
                                        className="h-5 w-5"
                                        fill="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <circle cx="5" cy="12" r="2" />
                                        <circle cx="12" cy="12" r="2" />
                                        <circle cx="19" cy="12" r="2" />
                                      </svg>
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-32">
                                    <DropdownMenuItem className="cursor-pointer">
                                      Edit
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      className="cursor-pointer text-red-600 focus:text-red-600"
                                      onClick={() => {
                                        setWorkUnits(
                                          workUnits.map((u) =>
                                            u.id === unit.id
                                              ? {
                                                  ...u,
                                                  workers: u.workers.filter(
                                                    (w) => w.id !== worker.id,
                                                  ),
                                                }
                                              : u,
                                          ),
                                        );
                                      }}
                                    >
                                      Remove
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Create Work Unit Dialog */}
        <Dialog open={workUnitDialogOpen} onOpenChange={setWorkUnitDialogOpen}>
          <DialogContent className="max-h-[calc(100vh-48px)] w-[95vw] max-w-[500px] overflow-y-auto p-4">
            <DialogHeader className="p-0">
              <DialogTitle>Create work unit</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 p-0">
              {/* Name and Workers needed row */}
              <div className="flex flex-col gap-1.5 sm:flex-row">
                <div className="w-full sm:flex-[3]">
                  <Label className="mb-2 block">Name</Label>
                  {isCreatingNewWorkType ? (
                    <div className="flex gap-2">
                      <Input
                        placeholder="Enter new work type name"
                        value={newWorkTypeName}
                        onChange={(e) => setNewWorkTypeName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleCreateNewWorkType();
                          } else if (e.key === "Escape") {
                            setIsCreatingNewWorkType(false);
                            setNewWorkTypeName("");
                          }
                        }}
                        autoFocus
                        className="h-10"
                      />
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleCreateNewWorkType}
                        className="h-10 shrink-0"
                      >
                        Create
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setIsCreatingNewWorkType(false);
                          setNewWorkTypeName("");
                        }}
                        className="h-10 shrink-0"
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <Combobox
                          value={workUnitName}
                          onChange={(val) => {
                            setWorkUnitName(val);
                            if (val) setWorkUnitNameError("");
                          }}
                          options={(config?.workTypes || []).map((workType: string) => ({
                            value: workType,
                            label: workType,
                          }))}
                          placeholder="Select work type"
                          error={!!workUnitNameError}
                        />
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setIsCreatingNewWorkType(true)}
                        className="text-muted-foreground h-10 shrink-0 whitespace-nowrap"
                      >
                        <svg
                          className="mr-1.5 h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4v16m8-8H4"
                          />
                        </svg>
                        Create new
                      </Button>
                    </div>
                  )}
                  {workUnitNameError && (
                    <p className="mt-1 text-sm text-red-500">{workUnitNameError}</p>
                  )}
                </div>
                <div className="w-full sm:w-[160px] sm:flex-none">
                  <Label className="mb-2 block">Workers needed</Label>
                  <div className="flex h-10 items-center rounded-md border border-gray-200 bg-white focus-within:border-[#7C12C1] focus-within:ring-1 focus-within:ring-[#7C12C1]">
                    <input
                      type="text"
                      value={workersNeeded}
                      onChange={(e) => setWorkersNeeded(e.target.value)}
                      className="h-full min-w-0 flex-1 appearance-none border-none bg-transparent px-3 text-sm outline-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-inner-spin-button]:hidden"
                    />
                    <div className="mr-3 flex shrink-0 flex-col">
                      <button
                        type="button"
                        onClick={() =>
                          setWorkersNeeded((prev) => String(Math.max(1, parseInt(prev || "1") + 1)))
                        }
                        className="flex h-3 items-center justify-center text-gray-400 hover:text-gray-600"
                      >
                        <svg
                          width="10"
                          height="6"
                          viewBox="0 0 10 6"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M1 5L5 1L9 5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setWorkersNeeded((prev) => String(Math.max(1, parseInt(prev || "1") - 1)))
                        }
                        className="flex h-3 items-center justify-center text-gray-400 hover:text-gray-600"
                      >
                        <svg
                          width="10"
                          height="6"
                          viewBox="0 0 10 6"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M1 1L5 5L9 1"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Job description */}
              <div>
                <Label className="mb-2 block">Job description</Label>
                <RichTextEditor
                  value={jobDescription}
                  onChange={setJobDescription}
                  placeholder="Describe the work unit and its responsibilities"
                  minHeight="120px"
                />
              </div>

              {/* English Level and Pay Rate row */}
              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                <div>
                  <Label className="mb-2 block">English Level</Label>
                  <Combobox
                    value={englishLevel}
                    onChange={setEnglishLevel}
                    options={[
                      { value: "no-requirement", label: "No requirement" },
                      { value: "basic", label: "Basic" },
                      { value: "intermediate", label: "Intermediate" },
                      { value: "advanced", label: "Advanced" },
                      { value: "fluent", label: "Fluent" },
                    ]}
                    placeholder="Select level"
                  />
                </div>
                <div>
                  <Label className="mb-2 block">Pay Rate</Label>
                  <div className="flex h-10 items-center rounded-md border border-gray-200 bg-white focus-within:border-[#7C12C1] focus-within:ring-1 focus-within:ring-[#7C12C1]">
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="e.g., 15.00"
                      value={payRate}
                      onChange={(e) => setPayRate(e.target.value)}
                      className="h-full min-w-0 flex-1 [appearance:textfield] border-none bg-transparent px-3 text-sm outline-none placeholder:text-gray-400 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    />
                    <span className="shrink-0 pr-1 text-sm text-gray-500">$/hour</span>
                    <div className="mr-3 flex shrink-0 flex-col">
                      <button
                        type="button"
                        onClick={() =>
                          setPayRate((prev) => (parseFloat(prev || "0") + 1).toFixed(2))
                        }
                        className="flex h-3 items-center justify-center text-gray-400 hover:text-gray-600"
                      >
                        <svg
                          width="10"
                          height="6"
                          viewBox="0 0 10 6"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M1 5L5 1L9 5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setPayRate((prev) => Math.max(0, parseFloat(prev || "0") - 1).toFixed(2))
                        }
                        className="flex h-3 items-center justify-center text-gray-400 hover:text-gray-600"
                      >
                        <svg
                          width="10"
                          height="6"
                          viewBox="0 0 10 6"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M1 1L5 5L9 1"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Start date */}
              <div>
                <Label className="mb-2 block">Start date</Label>
                <div className="relative flex h-10 items-center rounded-md border border-gray-200 bg-white focus-within:border-purple-600 focus-within:ring-1 focus-within:ring-purple-600">
                  <input
                    id="work-unit-start-date"
                    type="date"
                    value={workUnitStartDate}
                    onChange={(e) => setWorkUnitStartDate(e.target.value)}
                    className={`h-full w-full appearance-none border-none bg-transparent px-3 pr-10 text-sm outline-none [&::-webkit-calendar-picker-indicator]:pointer-events-none [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-inner-spin-button]:hidden ${!workUnitStartDate ? "[&::-webkit-datetime-edit]:opacity-0 [&::-webkit-datetime-edit-day-field]:opacity-0 [&::-webkit-datetime-edit-fields-wrapper]:opacity-0 [&::-webkit-datetime-edit-month-field]:opacity-0 [&::-webkit-datetime-edit-text]:opacity-0 [&::-webkit-datetime-edit-year-field]:opacity-0" : ""}`}
                  />
                  {!workUnitStartDate && (
                    <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-gray-500">
                      Select date
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => triggerPicker("work-unit-start-date")}
                    className={`absolute right-3 ${workUnitStartDate ? "text-gray-900" : "text-gray-400 hover:text-gray-600"}`}
                  >
                    <Calendar className="h-4 w-4" />
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  When this work unit becomes active. Leave empty if starting immediately
                </p>
              </div>

              {/* Schedule */}
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium text-gray-900">Schedule</Label>
                <div className="flex items-center gap-2">
                  <Switch checked={hasSchedule} onCheckedChange={setHasSchedule} />
                  <span className="text-sm text-gray-900">Set schedule</span>
                </div>
              </div>

              {hasSchedule && (
                <>
                  {/* Time type */}
                  <div className="flex flex-col gap-2">
                    <Label className="text-sm font-medium text-gray-900">Time type</Label>
                    <div className="flex w-full rounded-lg bg-gray-100 p-1">
                      <button
                        type="button"
                        onClick={() => setWorkUnitTimeType("same")}
                        className={`flex-1 rounded-md px-2 py-2 text-sm font-medium whitespace-nowrap transition-all ${
                          workUnitTimeType === "same"
                            ? "border border-purple-600 bg-white text-purple-600 shadow-sm"
                            : "text-gray-600 hover:text-gray-900"
                        }`}
                      >
                        Same all days
                      </button>
                      <button
                        type="button"
                        onClick={() => setWorkUnitTimeType("different")}
                        className={`flex-1 rounded-md px-2 py-2 text-sm font-medium whitespace-nowrap transition-all ${
                          workUnitTimeType === "different"
                            ? "border border-purple-600 bg-white text-purple-600 shadow-sm"
                            : "text-gray-600 hover:text-gray-900"
                        }`}
                      >
                        Different per day
                      </button>
                    </div>
                  </div>

                  {/* Same all days mode */}
                  {workUnitTimeType === "same" && (
                    <>
                      {/* Days checkboxes */}
                      <div className="flex flex-wrap gap-4">
                        {(["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const).map((day) => (
                          <Checkbox
                            key={day}
                            label={day.charAt(0).toUpperCase() + day.slice(1)}
                            checked={workUnitDays.includes(day)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setWorkUnitDays([...workUnitDays, day]);
                              } else {
                                setWorkUnitDays(workUnitDays.filter((d) => d !== day));
                              }
                            }}
                          />
                        ))}
                      </div>

                      {/* Time settings */}
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium text-gray-900">Time settings</Label>
                        <div className="flex items-center gap-2">
                          <Switch checked={workUnitAllDay} onCheckedChange={setWorkUnitAllDay} />
                          <span className="text-sm text-gray-900">All Day</span>
                        </div>
                      </div>

                      {/* Start and End time - only show if not All Day */}
                      {!workUnitAllDay && (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="mb-2 block">Start time</Label>
                            <div className="relative">
                              <div className="flex h-10 items-center rounded-md border border-gray-200 bg-white focus-within:border-purple-600 focus-within:ring-1 focus-within:ring-purple-600">
                                <input
                                  id="work-unit-start-time"
                                  type="time"
                                  value={workUnitStartTime}
                                  onChange={(e) => setWorkUnitStartTime(e.target.value)}
                                  className="h-full flex-1 border-none bg-transparent px-3 pr-10 text-sm outline-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-inner-spin-button]:hidden"
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => triggerPicker("work-unit-start-time")}
                                className={`absolute top-1/2 right-3 -translate-y-1/2 ${workUnitStartTime ? "text-gray-900" : "text-gray-400 hover:text-gray-600"}`}
                              >
                                <Clock className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          <div>
                            <Label className="mb-2 block">End time</Label>
                            <div className="relative">
                              <div className="flex h-10 items-center rounded-md border border-gray-200 bg-white focus-within:border-purple-600 focus-within:ring-1 focus-within:ring-purple-600">
                                <input
                                  id="work-unit-end-time"
                                  type="time"
                                  value={workUnitEndTime}
                                  onChange={(e) => setWorkUnitEndTime(e.target.value)}
                                  className="h-full flex-1 border-none bg-transparent px-3 pr-10 text-sm outline-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-inner-spin-button]:hidden"
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => triggerPicker("work-unit-end-time")}
                                className={`absolute top-1/2 right-3 -translate-y-1/2 ${workUnitEndTime ? "text-gray-900" : "text-gray-400 hover:text-gray-600"}`}
                              >
                                <Clock className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Different per day mode */}
                  {workUnitTimeType === "different" && (
                    <div className="space-y-1">
                      {(
                        [
                          { value: "sun", label: "Sunday" },
                          { value: "mon", label: "Monday" },
                          { value: "tue", label: "Tuesday" },
                          { value: "wed", label: "Wednesday" },
                          { value: "thu", label: "Thursday" },
                          { value: "fri", label: "Friday" },
                          { value: "sat", label: "Saturday" },
                        ] as const
                      ).map((day) => (
                        <div key={day.value}>
                          <div className="flex items-center justify-between py-1">
                            <Checkbox
                              label={day.label}
                              checked={workUnitDays.includes(day.value)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setWorkUnitDays([...workUnitDays, day.value]);
                                } else {
                                  setWorkUnitDays(workUnitDays.filter((d) => d !== day.value));
                                }
                              }}
                            />
                            {workUnitDays.includes(day.value) && (
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={workUnitDaySchedules[day.value]?.allDay ?? false}
                                  onCheckedChange={(checked) => {
                                    setWorkUnitDaySchedules((prev) => ({
                                      ...prev,
                                      [day.value]: { ...prev[day.value], allDay: checked },
                                    }));
                                  }}
                                />
                                <span className="text-sm text-gray-900">All Day</span>
                              </div>
                            )}
                          </div>
                          {/* Time inputs for this day - show if day is selected and not All Day */}
                          {workUnitDays.includes(day.value) &&
                            !workUnitDaySchedules[day.value]?.allDay && (
                              <div className="grid grid-cols-2 gap-4 pb-2 pl-6">
                                <div>
                                  <Label className="mb-2 block">Start time</Label>
                                  <div className="flex h-10 items-center rounded-md border border-gray-200 bg-white focus-within:border-purple-600 focus-within:ring-1 focus-within:ring-purple-600">
                                    <input
                                      type="time"
                                      value={workUnitDaySchedules[day.value]?.startTime ?? "09:00"}
                                      onChange={(e) => {
                                        setWorkUnitDaySchedules((prev) => ({
                                          ...prev,
                                          [day.value]: {
                                            ...prev[day.value],
                                            startTime: e.target.value,
                                          },
                                        }));
                                      }}
                                      className="h-full flex-1 appearance-none border-none bg-transparent px-3 text-sm outline-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-inner-spin-button]:hidden"
                                    />
                                  </div>
                                </div>
                                <div>
                                  <Label className="mb-2 block">End time</Label>
                                  <div className="flex h-10 items-center rounded-md border border-gray-200 bg-white focus-within:border-purple-600 focus-within:ring-1 focus-within:ring-purple-600">
                                    <input
                                      type="time"
                                      value={workUnitDaySchedules[day.value]?.endTime ?? "17:00"}
                                      onChange={(e) => {
                                        setWorkUnitDaySchedules((prev) => ({
                                          ...prev,
                                          [day.value]: {
                                            ...prev[day.value],
                                            endTime: e.target.value,
                                          },
                                        }));
                                      }}
                                      className="h-full flex-1 border-none bg-transparent px-3 text-sm outline-none"
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Action buttons */}
              <div className="flex justify-end gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setWorkUnitDialogOpen(false);
                    setWorkUnitNameError("");
                    setIsCreatingNewWorkType(false);
                    setNewWorkTypeName("");
                  }}
                  className="h-10 rounded-md"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    if (!workUnitName.trim()) {
                      setWorkUnitNameError("Please select or create work unit name");
                      return;
                    }
                    // Add work unit to list
                    const newWorkUnit: FormWorkUnit = {
                      id: Date.now().toString(),
                      name: workUnitName,
                      workersNeeded: parseInt(workersNeeded) || 1,
                      englishLevel: englishLevel,
                      payRate: payRate || "0.00",
                      startDate: workUnitStartDate,
                      description: jobDescription,
                      hasSchedule: hasSchedule,
                      days: workUnitDays,
                      allDay: workUnitAllDay,
                      startTime: workUnitStartTime,
                      endTime: workUnitEndTime,
                      expanded: false,
                      workers: [],
                    };
                    setWorkUnits([...workUnits, newWorkUnit]);
                    // Reset form
                    setWorkUnitName("");
                    setWorkersNeeded("1");
                    setJobDescription("");
                    setEnglishLevel("no-requirement");
                    setPayRate("");
                    setWorkUnitStartDate("");
                    setHasSchedule(false);
                    setWorkUnitDays(["mon", "tue", "wed"]);
                    setWorkUnitAllDay(false);
                    setWorkUnitStartTime("09:00");
                    setWorkUnitEndTime("17:00");
                    setWorkUnitDialogOpen(false);
                    setWorkUnitNameError("");
                    setIsCreatingNewWorkType(false);
                    setNewWorkTypeName("");
                  }}
                  className="h-10 rounded-md"
                >
                  Save
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Worker Dialog */}
        {addWorkerToUnitId &&
          (() => {
            const unit = workUnits.find((u) => u.id === addWorkerToUnitId);
            if (!unit) return null;

            const compatibleWorkUnit: FirestoreWorkUnit = {
              id: unit.id,
              name: unit.name,
              description: unit.description,
              workersNeeded: unit.workersNeeded,
              attachedWorkers: [],
              createdAt: Timestamp.now(),
              updatedAt: Timestamp.now(),
              schedule: {
                mode: "single",
                singleSchedule: {
                  allDay: unit.allDay,
                  startTime: unit.startTime,
                  endTime: unit.endTime,
                  days: unit.days.map((d) => {
                    const dayMap: Record<string, 0 | 1 | 2 | 3 | 4 | 5 | 6> = {
                      sun: 0,
                      mon: 1,
                      tue: 2,
                      wed: 3,
                      thu: 4,
                      fri: 5,
                      sat: 6,
                    };
                    return dayMap[d.toLowerCase()] as 0 | 1 | 2 | 3 | 4 | 5 | 6;
                  }),
                },
              },
            };

            return (
              <AddWorkerToWorkUnitDialog
                open={addWorkerDialogOpen}
                onOpenChange={(open) => {
                  setAddWorkerDialogOpen(open);
                  if (!open) setAddWorkerToUnitId(null);
                }}
                workUnit={compatibleWorkUnit}
                workUnitName={unit.name}
                assignedWorkers={unit.workers.map((w) => ({
                  workerId: w.id,
                  workerShifts: null,
                }))}
                workersNeeded={unit.workersNeeded}
                onAssignWorker={handleAssignWorker}
              />
            );
          })()}
      </div>
    </div>
  );
}
